import useUserUiPreferenceStorageValue from "../hooks/useUserUiPreferenceStorageValue.js";
import { userUiPreferenceStorage } from "../lib/userUiPreferenceStorage.js";
import React from "react";
import { useLocation, useNavigate } from "react-router";
import {
  CalendarDays,
  ChevronRight,
  FileText,
  History,
  LoaderCircle,
  Mail,
  Pencil,
  RectangleHorizontal,
  RectangleVertical,
  Save,
  Sheet,
  Truck,
  UserRound,
  Wheat,
} from "lucide-react";
import {
  ActionButton,
  joinClasses,
  sectionTagClassName,
  shellControlPastilleClassName,
} from "./ResultsPrimitives.jsx";
import { statusToneClasses } from "./resultsPrimitives/shared.js";
import WorkspacePageHeader from "./WorkspacePageHeader.jsx";
import ReportsDisabledActionHoverCard from "./ReportsDisabledActionHoverCard.jsx";
import ReportEmailActions from "./ReportEmailActions.jsx";
import { REPORT_EMAIL_BUTTON } from "./ReportEmailComposer.jsx";
import ReportsOrdersPdfCard from "./ReportsOrdersPdfCard.jsx";
import ReportsDriverSelect from "./ReportsDriverSelect.jsx";
import ReportsDispatchGanttCard from "./ReportsDispatchGanttCard.jsx";
import ReportsGeotabOrdersCard from "./ReportsGeotabOrdersCard.jsx";
import MeunerieManualTimesModal from "./MeunerieManualTimesModal.jsx";
import { useResultsExportTracker } from "../hooks/useResultsExportTracker.jsx";
import { useTruckSheetPdfExportTracker } from "../hooks/useTruckSheetPdfExportTracker.jsx";
import {
  downloadTruckPreloadReport,
  downloadMeunerieReport,
  getReportsActionsHistory,
  isExportApiError,
  loadMeunerieReportOptions,
  loadTruckSheetEmailContacts,
  loadTruckSheetEmailTemplate,
  markExportDocumentWindowFailed,
  MEUNERIE_TIME_MODE_DISPATCH,
  MEUNERIE_TIME_MODE_MANUAL,
  openExportDocumentWindow,
  sendMeunerieReportEmail,
  saveTruckSheetEmailTemplate,
  sendTruckSheetReportEmail,
} from "../lib/exportApi.js";
import useAccess from "../hooks/useAccess.jsx";
import {
  ACCESS_PERMISSION_KEYS,
  canExportDataManagerTable,
  canManageAutomations,
  canSendReportsEmail,
  hasPermission,
} from "../lib/accessControl.js";
import { fetchDataManagerState } from "../lib/dataManagerApi.js";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import {
  appendDispatchDateToUrl,
  normalizeDispatchPeriod,
  readDispatchDateFromSearch,
  readDispatchPeriodFromSearch,
  readLastDispatchContext,
  writeLastDispatchContext,
} from "../utils/dispatchDateUrls.js";
import { resolvePageSurfaceWidthClassName } from "../utils/pageSurfaceLayout.js";
import { buildDispatchSourceStaleNotice } from "../utils/resultsChromeData.js";
import { applyResultsDataToState, resolveAssignmentHasPreloadedPreviousDay } from "../utils/resultsWorkspaceData.js";
import { defaultReportEmailItem } from "../utils/reportEmailForm.js";
import "./ReportsPage.css";
import "./TraceabilityBanner.css";

const REPORTS_SURFACE_WIDTH_CLASS_NAME = resolvePageSurfaceWidthClassName("reports");
const REPORTS_TITLE_ICON_CLASS_NAME = "reports-card-icon";
const REPORTS_ACTION_ROW_CLASS_NAME = "reports-card-body";
const REPORTS_FILTER_FIELD_CLASS_NAME = "reports-field";
const REPORTS_FILTER_LABEL_CLASS_NAME = "reports-label";
const REPORTS_COMPACT_CONTROL_CLASS_NAME = "reports-control";
const REPORTS_ACTION_SLOT_CLASS_NAME = "reports-card-actions";
const REPORTS_EMAIL_ACTIONS_CLASS_NAME = "reports-email-actions";
const REPORTS_PRIMARY_ACTION_CLASS_NAME = "reports-primary-action";
const REPORTS_RESULTS_EXPORT_READY_ROUTE = "/reports?downloadExport=1";
const REPORTS_FLEET_PDF_READY_ROUTE = "/reports?downloadTruckSheetPdf=fleet";
const REPORTS_SELECTED_TRUCK_QUERY_KEY = "selected_truck_id";
const REPORTS_TRUCK_SHEET_DOWNLOAD_QUERY_KEY = "downloadTruckSheetPdf";
const REPORTS_HISTORY_PREVIEW_LIMIT = 10;
const REPORTS_HISTORY_SECONDARY_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center gap-1.5 rounded-[4px] px-3.5 py-1.75 text-[12px] font-semibold text-slate-700 transition-colors duration-150",
  shellControlPastilleClassName,
  "bg-white hover:border-slate-300 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-60",
]);
const ALL_MEUNERIES_LOOKUP_KEY = "__all__";
const ALL_MEUNERIES_OPTION = {
  label: "Toutes les meuneries",
  lookup_key: ALL_MEUNERIES_LOOKUP_KEY,
};
const MEUNERIE_TIME_MODE_OPTIONS = [
  { label: "Dispatch", title: "Heures calculées à partir du dispatch", value: MEUNERIE_TIME_MODE_DISPATCH },
  { label: "Manuelles", title: "Heures saisies avec « Saisir les heures »", value: MEUNERIE_TIME_MODE_MANUAL },
];
const ALL_TRUCKS_SELECTION_VALUE = "__all__";
const ALL_TRUCKS_OPTION = {
  label: "Tous les chauffeurs",
  value: ALL_TRUCKS_SELECTION_VALUE,
};
const DISPATCH_PERIOD_OPTIONS = [
  { label: "Jour", value: "day" },
  { label: "Soir", value: "evening" },
];
const TRUCK_SHEET_ORIENTATION_OPTIONS = [
  { ariaLabel: "Portrait", icon: RectangleVertical, value: "portrait" },
  { ariaLabel: "Paysage", icon: RectangleHorizontal, value: "landscape" },
];
const TRUCK_SHEET_COMPARTMENT_OPTIONS = [
  { label: "Avec", title: "Affiche la colonne Compartiments", value: true },
  { label: "Sans", title: "Retire la colonne Compartiments et élargit la colonne Note", value: false },
];
const TRUCK_SHEET_MAP_OPTIONS = [
  { label: "Avec", title: "Affiche la carte routière (format portrait)", value: true },
  { label: "Sans", title: "Retire la carte routière du rapport", value: false },
];
const TRUCK_PRELOAD_COMPARTMENT_OPTIONS = [
  { label: "Avec", title: "Affiche la colonne Compartiments", value: true },
  { label: "Sans", title: "Retire la colonne Compartiments du rapport", value: false },
];
const ON_CALL_DISPATCHER_STORAGE_KEY = "grain.reports.truckSheet.onCallDispatcher";
const EMAIL_ADDRESS_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_TRUCK_SHEET_EMAIL_SUBJECT_TEMPLATE = "Horaire pour le dispatch — {{dispatch_date}} — {{driver_name}}";
const DEFAULT_TRUCK_SHEET_EMAIL_MESSAGE_TEMPLATE = "Bonjour {{driver_name}},\n\n"
  + "Vous trouverez en pièce jointe l'horaire du Dispatch du {{dispatch_date}}.\n\n"
  + "Courriel envoyé automatiquement via TransIA";

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function buildNormalizedIdList(values) {
  if (!Array.isArray(values)) {
    return [];
  }

  const normalizedValues = [];
  values.forEach((value) => {
    const normalizedValue = normalizeInlineText(value);
    if (!normalizedValue || normalizedValues.includes(normalizedValue)) {
      return;
    }
    normalizedValues.push(normalizedValue);
  });
  return normalizedValues;
}

function isNightEmployeeShift(shift) {
  const normalizedShift = typeof shift === "string" ? shift.trim().toLowerCase() : "";
  return normalizedShift === "night" || normalizedShift === "soir";
}

function resolveInitialDispatchDate() {
  if (typeof window === "undefined") {
    return "";
  }

  return readDispatchDateFromSearch(window.location.search || "") || readLastDispatchContext().dispatch_date || "";
}

function readExplicitDispatchPeriodFromSearch(search = "") {
  const searchParams = new URLSearchParams(typeof search === "string" ? search : "");
  return searchParams.has("dispatch_period") ? readDispatchPeriodFromSearch(searchParams.toString()) : "";
}

function resolveInitialDispatchPeriod() {
  if (typeof window === "undefined") {
    return "day";
  }

  return readExplicitDispatchPeriodFromSearch(window.location.search || "")
    || normalizeDispatchPeriod(readLastDispatchContext().dispatch_period);
}

function readSelectedTruckIdFromSearch(search = "") {
  const searchParams = new URLSearchParams(typeof search === "string" ? search : "");
  return normalizeInlineText(searchParams.get(REPORTS_SELECTED_TRUCK_QUERY_KEY));
}

function buildSelectedTruckPdfReadyRoute(selectedTruckId = "", dispatchPeriod = "day") {
  const searchParams = new URLSearchParams();
  searchParams.set(REPORTS_TRUCK_SHEET_DOWNLOAD_QUERY_KEY, "selected");

  const normalizedSelectedTruckId = normalizeInlineText(selectedTruckId);
  if (normalizedSelectedTruckId) {
    searchParams.set(REPORTS_SELECTED_TRUCK_QUERY_KEY, normalizedSelectedTruckId);
  }

  if (normalizeDispatchPeriod(dispatchPeriod) === "evening") {
    searchParams.set("dispatch_period", "evening");
  }

  return `/reports?${searchParams.toString()}`;
}

function buildTruckSheetPdfExportFingerprint(baseFingerprint, {
  dispatchPeriod = "day",
  exportMode = "combined",
  fitOnePage = false,
  generationNonce = "",
  notes = "",
  orientation = "portrait",
  showCompartments = true,
  showMap = true,
  truckIds = [],
  revision = "report_v42",
} = {}) {
  const normalizedBaseFingerprint = normalizeInlineText(baseFingerprint);
  if (!normalizedBaseFingerprint) {
    return "";
  }

  const normalizedTruckIds = Array.isArray(truckIds)
    ? truckIds.map((truckId) => normalizeInlineText(truckId)).filter(Boolean)
    : [];
  const normalizedDispatchPeriod = normalizeDispatchPeriod(dispatchPeriod);
  const normalizedOrientation = orientation === "landscape" ? "landscape" : "portrait";
  const fitOnePageScope = fitOnePage ? "fit1" : "fit0";
  const noteFingerprint = normalizeInlineText(notes).replace(/\s+/g, " ");
  const normalizedGenerationNonce = normalizeInlineText(generationNonce);
  const scope = `${normalizedBaseFingerprint}::truck_sheet_pdf::${revision}::${normalizedDispatchPeriod}::${exportMode}::${normalizedOrientation}::${fitOnePageScope}::${normalizedTruckIds.length ? normalizedTruckIds.join(",") : "all"}`;
  const fingerprintParts = [scope];
  if (showCompartments === false) {
    fingerprintParts.push("nocmp");
  }
  if (showMap === false) {
    fingerprintParts.push("nomap");
  }
  if (noteFingerprint) {
    fingerprintParts.push(`notes::${noteFingerprint}`);
  }
  if (normalizedGenerationNonce) {
    fingerprintParts.push(`run::${normalizedGenerationNonce}`);
  }
  return fingerprintParts.join("::");
}

function isAllMeuneriesSelection(lookupKey = "") {
  return normalizeInlineText(lookupKey) === ALL_MEUNERIES_LOOKUP_KEY;
}

function isAllTrucksSelection(truckId = "") {
  return normalizeInlineText(truckId) === ALL_TRUCKS_SELECTION_VALUE;
}

function resolveFeedbackToneClassName(tone) {
  if (tone === "danger") {
    return "border border-[var(--color-border-danger-subtle)] bg-danger-soft text-danger";
  }

  if (tone === "success") {
    return "border border-[var(--color-border-badge-subtle)] bg-[var(--grain-badge-bg)] text-[var(--grain-badge-text)]";
  }

  return "border border-line-soft bg-white text-muted-strong";
}

function isValidEmailAddress(value) {
  return EMAIL_ADDRESS_PATTERN.test(normalizeInlineText(value));
}

function buildDefaultMeunerieEmailSubject({ dispatchDate = "", meunerieLabel = "" } = {}) {
  const normalizedDispatchDate = normalizeInlineText(dispatchDate);
  const normalizedMeunerieLabel = normalizeInlineText(meunerieLabel) || "Rapport Meunerie";

  if (normalizedDispatchDate) {
    return `Rapport Meunerie - ${normalizedMeunerieLabel} - ${normalizedDispatchDate}`;
  }

  return `Rapport Meunerie - ${normalizedMeunerieLabel}`;
}

function buildDefaultTruckSheetEmailSubject({
  dispatchDate = "",
  driverName = "",
} = {}) {
  const normalizedDispatchDate = normalizeInlineText(dispatchDate);
  const normalizedDriverName = normalizeInlineText(driverName) || "Chauffeur";

  return `Horaire pour le dispatch — ${normalizedDispatchDate || "date"} — ${normalizedDriverName}`;
}

function renderTruckSheetEmailTemplate(templateText, { dispatchDate = "", driverName = "" } = {}) {
  const normalizedTemplate = typeof templateText === "string" ? templateText : "";
  const values = {
    dispatch_date: normalizeInlineText(dispatchDate),
    driver_name: normalizeInlineText(driverName),
  };

  return normalizedTemplate.replace(/{{\s*([a-z_]+)\s*}}/g, (match, token) => (
    Object.prototype.hasOwnProperty.call(values, token) ? values[token] : match
  ));
}

function buildDefaultTruckSheetEmailMessage({ dispatchDate = "", driverName = "" } = {}) {
  return renderTruckSheetEmailTemplate(
    DEFAULT_TRUCK_SHEET_EMAIL_MESSAGE_TEMPLATE,
    { dispatchDate, driverName },
  );
}


function isMissingLatestOptimizationSnapshot(payload) {
  return Boolean(
    payload
      && typeof payload === "object"
      && !Array.isArray(payload)
      && payload.code === "resource_not_found"
      && payload.resource === "latest_optimization",
  );
}

function resolveResultsSnapshotRequestErrorMessage(response, payload) {
  const payloadMessage = normalizeInlineText(payload?.error)
    || normalizeInlineText(payload?.message)
    || normalizeInlineText(payload?.detail);

  if (payloadMessage) {
    return payloadMessage;
  }

  if (response?.status) {
    return `Erreur ${response.status} pendant le chargement du dispatch.`;
  }

  return "Impossible de charger le dispatch lié à cette date.";
}

function ReportsEmailTriggerButton({
  disabled = false,
  onClick,
}) {
  return (
    <button
      type="button"
      title="Envoyer par courriel"
      disabled={disabled}
      onClick={onClick}
      className={REPORT_EMAIL_BUTTON}
    >
      <Mail className="h-3.5 w-3.5" />
      Envoyer par courriel
    </button>
  );
}

function TruckSheetPdfActionButton({
  disabled = false,
  isLoading = false,
  label = "",
  onClick,
}) {
  return (
    <ActionButton
      tone="optimizer"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      isLoading={isLoading}
      loadingLabel="Génération..."
      className={REPORTS_PRIMARY_ACTION_CLASS_NAME}
    >
      {label}
    </ActionButton>
  );
}

function buildReportsExportContext(resultsPayload, { dispatchPeriod = "day", selectedTruckId = "", selectedTruckLabel = "" } = {}) {
  const safePayload = resultsPayload && typeof resultsPayload === "object" ? resultsPayload : {};
  const exportContext = safePayload.export_context && typeof safePayload.export_context === "object"
    ? safePayload.export_context
    : {};
  const planningSummary = safePayload.planning_summary && typeof safePayload.planning_summary === "object"
    ? safePayload.planning_summary
    : {};
  const snapshotStatus = normalizeInlineText(exportContext?.snapshot_status).toLowerCase() === "stale"
    ? "stale"
    : "ready";
  const snapshotGeneratedAt = normalizeInlineText(exportContext?.generated_at);
  const planningUpdatedAt = normalizeInlineText(planningSummary?.updated_at);
  const hasSnapshot = !isMissingLatestOptimizationSnapshot(safePayload);
  const sourceStaleNotice = hasSnapshot
    ? buildDispatchSourceStaleNotice(safePayload, { planningUpdatedAt })
    : null;
  const allowStaleSnapshotActions = sourceStaleNotice !== null;
  const shouldBlockStaleSnapshotActions = hasSnapshot && snapshotStatus === "stale" && !allowStaleSnapshotActions;
  const normalizedDispatchPeriod = normalizeDispatchPeriod(dispatchPeriod);
  const exportFingerprint = hasSnapshot && (snapshotGeneratedAt || planningUpdatedAt)
    ? `${snapshotGeneratedAt}::${planningUpdatedAt}::${normalizedDispatchPeriod}`
    : "";
  const truckSheetPdfExportRevision = "report_v42";
  const normalizedSelectedTruckId = normalizeInlineText(selectedTruckId);
  const normalizedSelectedTruckLabel = normalizeInlineText(selectedTruckLabel) || normalizedSelectedTruckId || "Camion sélectionné";

  return {
    allowStaleSnapshotActions,
    hasSnapshot,
    resultsExport: {
      blockedReason: shouldBlockStaleSnapshotActions
        ? "Le snapshot d'optimisation est obsolète. Relancez l'optimisation avant d'exporter les résultats."
        : "",
      fingerprint: exportFingerprint,
      snapshotStatus,
    },
    sourceStaleNotice,
    truckSheetPdf: {
      blockedReason: shouldBlockStaleSnapshotActions
        ? "Le snapshot d'optimisation est obsolète. Relancez l'optimisation avant d'exporter les fiches PDF chauffeur."
        : "",
      fleetFingerprint: exportFingerprint
        ? `${exportFingerprint}::truck_sheet_pdf::${truckSheetPdfExportRevision}::${normalizedDispatchPeriod}::combined::all`
        : "",
      selectedFingerprint: exportFingerprint && normalizedSelectedTruckId
        ? `${exportFingerprint}::truck_sheet_pdf::${truckSheetPdfExportRevision}::${normalizedDispatchPeriod}::single::${normalizedSelectedTruckId}`
        : "",
      selectedTruckId: normalizedSelectedTruckId,
      selectedTruckLabel: normalizedSelectedTruckLabel,
      snapshotStatus,
    },
  };
}

function formatReportsHistoryTimestamp(value) {
  if (!value) {
    return "Horodatage indisponible";
  }

  const resolvedDate = new Date(value);
  if (Number.isNaN(resolvedDate.getTime())) {
    return "Horodatage indisponible";
  }

  return new Intl.DateTimeFormat("fr-CA", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
  }).format(resolvedDate);
}

function resolveReportsHistoryActorLabel(entry) {
  const actorEmail = normalizeInlineText(entry?.actor_email);
  if (actorEmail) {
    return actorEmail;
  }

  const actorUserId = normalizeInlineText(entry?.actor_user_id);
  return actorUserId || "Compte système";
}

function resolveReportsHistoryActionLabel(entry) {
  const actionLabel = normalizeInlineText(entry?.action_label);
  if (actionLabel) {
    return actionLabel;
  }

  const action = normalizeInlineText(entry?.action);
  return action || "Action Rapports";
}

function resolveReportsHistoryDetail(entry) {
  const summary = normalizeInlineText(entry?.summary);
  if (summary) {
    return summary;
  }

  const dispatchDate = normalizeInlineText(entry?.dispatch_date);
  return dispatchDate ? `Dispatch ${dispatchDate}` : "—";
}

function resolveReportsHistoryErrorMessage(error) {
  if (isExportApiError(error) && error.message) {
    return error.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Impossible de charger la traçabilité des rapports.";
}

function ReportsHistorySection({
  errorMessage = "",
  historyItems = [],
  isCollapsed = false,
  isFullPage = false,
  isLoading = false,
  isRefreshing = false,
  onCloseFullPage = null,
  onOpenFullPage = null,
  onToggleCollapsed = null,
}) {
  const visibleRows = isFullPage
    ? historyItems
    : historyItems.slice(0, REPORTS_HISTORY_PREVIEW_LIMIT);
  const historySummaryLabel = isLoading && historyItems.length === 0
    ? "Chargement de la traçabilité..."
    : `${historyItems.length} action${historyItems.length === 1 ? "" : "s"} Rapports`;

  return (
    <article className={joinClasses([
      "traceability-panel reports-history rounded-[4px] border border-black/6 bg-white px-3 py-2.5 shadow-[0_10px_22px_rgba(15,23,42,0.04)]",
      isFullPage ? "min-h-[70vh]" : "",
    ])}>
      <div className="traceability-banner">
        <div className="traceability-heading">
          {!isFullPage && typeof onToggleCollapsed === "function" ? (
            <button
              type="button"
              onClick={onToggleCollapsed}
              aria-label={isCollapsed ? "Déplier la traçabilité des rapports" : "Replier la traçabilité des rapports"}
              aria-expanded={!isCollapsed}
              aria-controls="reports-history-content"
              className="traceability-toggle inline-flex h-7 w-7 items-center justify-center rounded-[4px] border border-slate-200 bg-white text-slate-500 transition-colors duration-150 hover:border-slate-300 hover:text-slate-950"
            >
              <ChevronRight size={13} className={isCollapsed ? "" : "rotate-90"} />
            </button>
          ) : null}
          <span className="traceability-icon">
            <History size={15} />
          </span>
          <div className="traceability-copy">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Traçabilité</p>
            <h2 className="text-[0.98rem] font-semibold tracking-[-0.02em] text-slate-950">
              {isFullPage ? "Traçabilité complète · Rapports" : "Dernières actions"}
            </h2>
          </div>
        </div>

        <div className="traceability-actions">
          <span>{historySummaryLabel}</span>
          {isRefreshing ? (
            <span className={joinClasses([
              "inline-flex items-center gap-1.5 rounded-[4px] px-2 py-0.5 text-[9px] font-semibold",
              statusToneClasses.info,
            ])}>
              <LoaderCircle size={12} className="animate-spin" aria-hidden="true" />
              Actualisation…
            </span>
          ) : null}
          {isFullPage && typeof onCloseFullPage === "function" ? (
            <button type="button" onClick={onCloseFullPage} className={REPORTS_HISTORY_SECONDARY_ACTION_CLASS_NAME}>
              Retour aux rapports
            </button>
          ) : !isFullPage && historyItems.length > 0 && typeof onOpenFullPage === "function" ? (
            <button type="button" onClick={onOpenFullPage} className={REPORTS_HISTORY_SECONDARY_ACTION_CLASS_NAME}>
              Voir la traçabilité complète
            </button>
          ) : null}
        </div>
      </div>

      {!isCollapsed || isFullPage ? (
        <div id="reports-history-content" className="mt-2">
          {errorMessage ? (
            <p className={joinClasses([
              "rounded-[4px] border px-2.5 py-1.5 text-[11px] leading-4",
              statusToneClasses.warning,
            ])}>
              {errorMessage}
            </p>
          ) : null}

          {isLoading && historyItems.length === 0 ? (
            <p className="text-[11px] leading-5 text-slate-500">Lecture de la traçabilité des rapports...</p>
          ) : null}

          {!isLoading && historyItems.length === 0 && !errorMessage ? (
            <p className="text-[11px] leading-5 text-slate-500">Aucune action Rapport n&apos;a encore été enregistrée.</p>
          ) : null}

          {visibleRows.length > 0 ? (
            <div className="reports-history-table overflow-auto rounded-[4px] border border-slate-200/90">
              <table className="w-full table-fixed border-collapse text-[11px] text-slate-700">
                <thead className="sticky top-0 z-10 bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.95))]">
                  <tr>
                    <th className="w-36 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Date</th>
                    <th className="w-40 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Utilisateur</th>
                    <th className="w-52 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Action</th>
                    <th className="w-full min-w-0 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Détail</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {visibleRows.map((entry) => {
                    const rowKey = normalizeInlineText(entry?.id) || `${entry?.created_at}-${entry?.action}`;
                    const dateLabel = formatReportsHistoryTimestamp(entry?.created_at);
                    const actorLabel = resolveReportsHistoryActorLabel(entry);
                    const actionLabel = resolveReportsHistoryActionLabel(entry);
                    const detailLabel = resolveReportsHistoryDetail(entry);

                    return (
                      <tr key={rowKey} className="odd:bg-slate-50/50">
                        <td data-label="Date" className="w-36 px-3 py-2 whitespace-nowrap text-[10.5px] text-slate-500" title={dateLabel}>
                          {dateLabel}
                        </td>
                        <td data-label="Utilisateur" className="w-40 px-3 py-2">
                          <span className="block max-w-full truncate text-[11px] text-slate-700" title={actorLabel}>{actorLabel}</span>
                        </td>
                        <td data-label="Action" className="w-52 px-3 py-2">
                          <span className="block max-w-full truncate text-[11px] font-semibold text-slate-900" title={actionLabel}>{actionLabel}</span>
                        </td>
                        <td data-label="Détail" className="w-full min-w-0 px-3 py-2">
                          <span className="block max-w-full truncate text-[11px] text-slate-600" title={detailLabel}>{detailLabel}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}

          {!isFullPage && visibleRows.length > 0 ? (
            <p className="mt-2 text-[10px] leading-4 text-slate-400">
              Aperçu limité aux {REPORTS_HISTORY_PREVIEW_LIMIT} dernières actions Rapports.
            </p>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function MeunerieEmailModal({
  dispatchDate = "",
  errorMessage = "",
  isOpen = false,
  isSending = false,
  meunerieLabel = "",
  onClose,
  onRecipientEmailChange,
  onSend,
  onSubjectChange,
  recipientEmail = "",
  subject = "",
}) {
  const recipientInputRef = React.useRef(null);
  const canSubmit = !isSending && isValidEmailAddress(recipientEmail) && Boolean(normalizeInlineText(subject));

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape" && !isSending) {
        event.preventDefault();
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isSending, onClose]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => {
      recipientInputRef.current?.focus();
    });

    return () => {
      document.body.style.overflow = previousOverflow;
      window.cancelAnimationFrame(frame);
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center bg-slate-950/45 px-4 py-6"
      onMouseDown={(event) => {
        if (!isSending && event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <section
        aria-modal="true"
        className="w-full max-w-[min(94vw,52rem)] rounded-[4px] border border-black/6 bg-white p-4 shadow-[0_28px_80px_rgba(15,23,42,0.16)]"
        role="dialog"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="grid gap-1">
            <span className={sectionTagClassName}>Rapport Meunerie</span>
            <h3 className="text-[1.08rem] font-semibold tracking-[-0.03em] text-slate-950">Envoyer par courriel</h3>
            <p className="text-[0.82rem] leading-[1.4] text-slate-600">
              Le PDF sera régénéré côté serveur puis envoyé en pièce jointe à l'adresse saisie.
            </p>
            <p className="text-[0.75rem] font-medium leading-5 text-slate-500">
              {meunerieLabel || "Meunerie sélectionnée"}
              {dispatchDate ? ` • ${dispatchDate}` : ""}
            </p>
          </div>

          <button
            type="button"
            className="rounded-[4px] border border-line-soft bg-surface-muted px-3 py-1 text-[0.86rem] font-semibold text-muted-strong transition hover:border-accent hover:bg-white hover:text-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isSending}
            onClick={() => {
              onClose?.();
            }}
          >
            Fermer
          </button>
        </div>

        <form
          className="mt-3 grid gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit) {
              return;
            }
            onSend?.();
          }}
        >
          <label className="grid gap-1.5 rounded-[4px] border border-[var(--shell-control-pastille-border,#e2e8f0)] bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.8))] px-3 py-2">
            <span className="text-[0.69rem] font-black uppercase tracking-[0.12em] text-slate-500">Destinataire</span>
            <input
              ref={recipientInputRef}
              type="email"
              value={recipientEmail}
              onChange={(event) => onRecipientEmailChange?.(event.target.value)}
              placeholder="nom@entreprise.com"
              disabled={isSending}
              className="min-h-10 rounded-[4px] border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-border-accent-muted focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
            />
          </label>

          <label className="grid gap-1.5 rounded-[4px] border border-[var(--shell-control-pastille-border,#e2e8f0)] bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.8))] px-3 py-2">
            <span className="text-[0.69rem] font-black uppercase tracking-[0.12em] text-slate-500">Sujet</span>
            <input
              type="text"
              value={subject}
              onChange={(event) => onSubjectChange?.(event.target.value)}
              disabled={isSending}
              className="min-h-10 rounded-[4px] border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-border-accent-muted focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
            />
          </label>

          {errorMessage ? (
            <div className="rounded-[4px] border border-border-danger-subtle bg-danger-soft px-3 py-2 text-[0.82rem] leading-5 text-danger">
              {errorMessage}
            </div>
          ) : null}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <ActionButton
              tone="secondary"
              size="sm"
              onClick={() => {
                onClose?.();
              }}
              disabled={isSending}
            >
              Annuler
            </ActionButton>
            <ActionButton
              tone="primary"
              size="sm"
              type="submit"
              disabled={!canSubmit}
              isLoading={isSending}
              loadingLabel="Envoi..."
            >
              Envoyer
            </ActionButton>
          </div>
        </form>
      </section>
    </div>
  );
}

function TruckSheetEmailModal({
  dispatchDate = "",
  dispatchPeriodLabel = "",
  driverName = "",
  errorMessage = "",
  isOpen = false,
  isSending = false,
  onCallDispatcher = "",
  onClose,
  onOnCallDispatcherChange,
  onRecipientEmailChange,
  onSend,
  message = "",
  recipientEmail = "",
  scopeLabel = "",
  subject = "",
}) {
  const recipientInputRef = React.useRef(null);
  const canSubmit = !isSending
    && isValidEmailAddress(recipientEmail)
    && Boolean(normalizeInlineText(subject))
    && Boolean(normalizeInlineText(message));

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape" && !isSending) {
        event.preventDefault();
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isSending, onClose]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => {
      recipientInputRef.current?.focus();
    });

    return () => {
      document.body.style.overflow = previousOverflow;
      window.cancelAnimationFrame(frame);
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center bg-slate-950/45 px-4 py-6"
      onMouseDown={(event) => {
        if (!isSending && event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <section
        aria-modal="true"
        className="w-full max-w-[min(94vw,52rem)] rounded-[4px] border border-black/6 bg-white p-4 shadow-[0_28px_80px_rgba(15,23,42,0.16)]"
        role="dialog"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="grid gap-1">
            <span className={sectionTagClassName}>Horaire chauffeur</span>
            <h3 className="text-[1.08rem] font-semibold tracking-[-0.03em] text-slate-950">Envoyer par courriel</h3>
            <p className="text-[0.82rem] leading-[1.4] text-slate-600">
              Le PDF sera régénéré côté serveur puis envoyé en pièce jointe à l'adresse saisie.
            </p>
            <p className="text-[0.75rem] font-medium leading-5 text-slate-500">
              {scopeLabel || "Camion sélectionné"}
              {dispatchPeriodLabel ? ` • ${dispatchPeriodLabel}` : ""}
              {dispatchDate ? ` • ${dispatchDate}` : ""}
            </p>
            <div className="mt-1 rounded-[4px] border border-accent/15 bg-accent-soft px-3 py-2 text-sm text-slate-800">
              <strong>Chauffeur :</strong> {driverName || "Nom introuvable"}
            </div>
          </div>

          <button
            type="button"
            className="rounded-[4px] border border-line-soft bg-surface-muted px-3 py-1 text-[0.86rem] font-semibold text-muted-strong transition hover:border-accent hover:bg-white hover:text-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isSending}
            onClick={() => {
              onClose?.();
            }}
          >
            Fermer
          </button>
        </div>

        <form
          className="mt-3 grid gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit) {
              return;
            }
            onSend?.();
          }}
        >
          <label className="grid gap-1.5 rounded-[4px] border border-[var(--shell-control-pastille-border,#e2e8f0)] bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.8))] px-3 py-2">
            <span className="text-[0.69rem] font-black uppercase tracking-[0.12em] text-slate-500">Destinataire</span>
            <input
              ref={recipientInputRef}
              type="email"
              value={recipientEmail}
              onChange={(event) => onRecipientEmailChange?.(event.target.value)}
              placeholder="nom@entreprise.com"
              disabled={isSending}
              className="min-h-10 rounded-[4px] border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-border-accent-muted focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
            />
          </label>

          <label className="grid gap-1.5 rounded-[4px] border border-[var(--shell-control-pastille-border,#e2e8f0)] bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.8))] px-3 py-2">
            <span className="text-[0.69rem] font-black uppercase tracking-[0.12em] text-slate-500">Répartiteur de garde</span>
            <input
              value={onCallDispatcher}
              onChange={(event) => onOnCallDispatcherChange?.(event.target.value)}
              maxLength={120}
              placeholder="Nom du répartiteur, imprimé dans le haut du PDF"
              disabled={isSending}
              className="min-h-10 rounded-[4px] border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-border-accent-muted focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
            />
          </label>

          <label className="grid gap-1.5 rounded-[4px] border border-[var(--shell-control-pastille-border,#e2e8f0)] bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.8))] px-3 py-2">
            <span className="text-[0.69rem] font-black uppercase tracking-[0.12em] text-slate-500">Sujet</span>
            <input
              type="text"
              value={subject}
              placeholder="Sujet du courriel"
              readOnly
              className="min-h-10 rounded-[4px] border border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-900 outline-none"
            />
          </label>

          <div className="grid gap-1.5 rounded-[4px] border border-[var(--shell-control-pastille-border,#e2e8f0)] bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.8))] px-3 py-2">
            <span className="text-[0.69rem] font-black uppercase tracking-[0.12em] text-slate-500">Message</span>
            <pre className="whitespace-pre-wrap break-words rounded-[4px] border border-slate-200 bg-slate-50 px-3 py-2 text-sm leading-6 text-slate-700">{message}</pre>
          </div>

          {errorMessage ? (
            <div className="rounded-[4px] border border-border-danger-subtle bg-danger-soft px-3 py-2 text-[0.82rem] leading-5 text-danger">
              {errorMessage}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-end gap-2">
            <ActionButton
              tone="secondary"
              size="sm"
              type="button"
              disabled={isSending}
              onClick={() => {
                onClose?.();
              }}
            >
              Annuler
            </ActionButton>
            <ActionButton
              tone="primary"
              size="sm"
              type="submit"
              disabled={!canSubmit}
              isLoading={isSending}
              loadingLabel="Envoi..."
            >
              Envoyer
            </ActionButton>
          </div>
        </form>
      </section>
    </div>
  );
}

function TruckSheetEmailTemplateModal({
  dispatchDate = "",
  driverName = "",
  errorMessage = "",
  isOpen = false,
  isSaving = false,
  messageTemplate = "",
  onClose,
  onMessageTemplateChange,
  onSave,
  onSubjectTemplateChange,
  subjectTemplate = "",
}) {
  const previewDriverName = normalizeInlineText(driverName) || "Nom du chauffeur";
  const subjectPreview = renderTruckSheetEmailTemplate(subjectTemplate, {
    dispatchDate,
    driverName: previewDriverName,
  });
  const messagePreview = renderTruckSheetEmailTemplate(messageTemplate, {
    dispatchDate,
    driverName: previewDriverName,
  });
  const canSubmit = !isSaving && Boolean(normalizeInlineText(subjectTemplate)) && Boolean(normalizeInlineText(messageTemplate));

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape" && !isSaving) {
        event.preventDefault();
        onClose?.();
      }
    };
    document.addEventListener("keydown", handleEscape);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, isSaving, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center bg-slate-950/45 px-4 py-6"
      onMouseDown={(event) => {
        if (!isSaving && event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <section
        aria-modal="true"
        className="w-full max-w-[min(94vw,58rem)] rounded-[4px] border border-black/6 bg-white p-4 shadow-[0_28px_80px_rgba(15,23,42,0.16)]"
        role="dialog"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="grid gap-1">
            <span className={sectionTagClassName}>Horaire chauffeur</span>
            <h3 className="text-[1.08rem] font-semibold tracking-[-0.03em] text-slate-950">Personnaliser le courriel</h3>
            <p className="text-[0.82rem] leading-[1.4] text-slate-600">
              Ce modèle sera partagé par tous les répartiteurs autorisés.
            </p>
          </div>
          <button
            type="button"
            className="rounded-[4px] border border-line-soft bg-surface-muted px-3 py-1 text-[0.86rem] font-semibold text-muted-strong transition hover:border-accent hover:bg-white hover:text-accent disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isSaving}
            onClick={() => onClose?.()}
          >
            Fermer
          </button>
        </div>

        <form
          className="mt-3 grid gap-3 min-[900px]:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit) {
              onSave?.();
            }
          }}
        >
          <div className="grid gap-3">
            <label className="grid gap-1.5 rounded-[4px] border border-[var(--shell-control-pastille-border,#e2e8f0)] bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.8))] px-3 py-2">
              <span className="text-[0.69rem] font-black uppercase tracking-[0.12em] text-slate-500">Objet du modèle</span>
              <input
                type="text"
                value={subjectTemplate}
                onChange={(event) => onSubjectTemplateChange?.(event.target.value)}
                disabled={isSaving}
                maxLength={255}
                className="min-h-10 rounded-[4px] border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-border-accent-muted focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100"
              />
            </label>
            <label className="grid gap-1.5 rounded-[4px] border border-[var(--shell-control-pastille-border,#e2e8f0)] bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.8))] px-3 py-2">
              <span className="text-[0.69rem] font-black uppercase tracking-[0.12em] text-slate-500">Message du modèle</span>
              <textarea
                value={messageTemplate}
                onChange={(event) => onMessageTemplateChange?.(event.target.value)}
                disabled={isSaving}
                rows={8}
                maxLength={10000}
                className="rounded-[4px] border border-slate-200 bg-white px-3 py-2 text-sm leading-6 text-slate-900 outline-none transition focus:border-border-accent-muted focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100"
              />
            </label>
            <p className="text-xs leading-5 text-slate-500">
              Variables disponibles : <code className="font-semibold">&#123;&#123;driver_name&#125;&#125;</code> et <code className="font-semibold">&#123;&#123;dispatch_date&#125;&#125;</code>.
            </p>
          </div>

          <div className="grid content-start gap-2 rounded-[4px] border border-accent/15 bg-accent-soft p-3">
            <span className="text-[0.69rem] font-black uppercase tracking-[0.12em] text-accent-strong">Aperçu</span>
            <div className="grid gap-1 rounded-[4px] bg-white/75 p-3">
              <span className="text-xs font-semibold text-slate-500">Objet</span>
              <strong className="break-words text-sm text-slate-900">{subjectPreview}</strong>
            </div>
            <pre className="whitespace-pre-wrap break-words rounded-[4px] bg-white/75 p-3 text-sm leading-6 text-slate-700">{messagePreview}</pre>
          </div>

          {errorMessage ? (
            <div className="min-[900px]:col-span-2 rounded-[4px] border border-border-danger-subtle bg-danger-soft px-3 py-2 text-[0.82rem] leading-5 text-danger">
              {errorMessage}
            </div>
          ) : null}

          <div className="min-[900px]:col-span-2 flex flex-wrap items-center justify-end gap-2">
            <ActionButton tone="secondary" size="sm" type="button" disabled={isSaving} onClick={() => onClose?.()}>
              Annuler
            </ActionButton>
            <ActionButton
              tone="primary"
              size="sm"
              type="submit"
              disabled={!canSubmit}
              isLoading={isSaving}
              loadingLabel="Sauvegarde..."
              className="shrink-0 whitespace-nowrap"
            >
              <span className="inline-flex items-center gap-2">
                <Save size={15} aria-hidden="true" className="shrink-0" />
                Enregistrer
              </span>
            </ActionButton>
          </div>
        </form>
      </section>
    </div>
  );
}

function TruckSheetNoteModal({
  dispatchDate = "",
  dispatchPeriodLabel = "",
  generateLabel = "Préparer la fiche",
  isOpen = false,
  note = "",
  onCallDispatcher = "",
  onClose,
  onGenerate,
  onNoteChange,
  onOnCallDispatcherChange,
  scopeLabel = "",
}) {
  const noteInputRef = React.useRef(null);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => {
      noteInputRef.current?.focus();
    });

    return () => {
      document.body.style.overflow = previousOverflow;
      window.cancelAnimationFrame(frame);
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center bg-slate-950/45 px-4 py-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <section
        aria-modal="true"
        className="w-full max-w-lg rounded-[4px] border border-black/6 bg-white p-4 shadow-[0_28px_80px_rgba(15,23,42,0.16)]"
        role="dialog"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="grid gap-1">
            <span className={sectionTagClassName}>Horaire chauffeur</span>
            <h3 className="text-[1.08rem] font-semibold tracking-[-0.03em] text-slate-950">Ajouter une note à la fiche</h3>
            <p className="text-[0.82rem] leading-[1.4] text-slate-600">
              Le répartiteur de garde est imprimé dans le haut du PDF, la note dans la section Notes.
            </p>
            <p className="text-[0.75rem] font-medium leading-5 text-slate-500">
              {scopeLabel || "Horaire chauffeur"}
              {dispatchPeriodLabel ? ` • ${dispatchPeriodLabel}` : ""}
              {dispatchDate ? ` • ${dispatchDate}` : ""}
            </p>
          </div>

          <button
            type="button"
            className="rounded-[4px] border border-line-soft bg-surface-muted px-3 py-1 text-[0.86rem] font-semibold text-muted-strong transition hover:border-accent hover:bg-white hover:text-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            onClick={() => {
              onClose?.();
            }}
          >
            Fermer
          </button>
        </div>

        <form
          className="mt-3 grid gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            onGenerate?.();
          }}
        >
          <label className="grid gap-1.5 rounded-[4px] border border-[var(--shell-control-pastille-border,#e2e8f0)] bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.8))] px-3 py-2">
            <span className="text-[0.69rem] font-black uppercase tracking-[0.12em] text-slate-500">Répartiteur de garde</span>
            <input
              value={onCallDispatcher}
              onChange={(event) => onOnCallDispatcherChange?.(event.target.value)}
              maxLength={120}
              placeholder="Nom du répartiteur"
              className="rounded-[4px] border border-slate-200 bg-white px-3 py-2 text-sm font-medium leading-5 text-slate-900 outline-none transition focus:border-border-accent-muted focus:ring-2 focus:ring-border-accent-subtle"
            />
          </label>

          <label className="grid gap-1.5 rounded-[4px] border border-[var(--shell-control-pastille-border,#e2e8f0)] bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.8))] px-3 py-2">
            <span className="text-[0.69rem] font-black uppercase tracking-[0.12em] text-slate-500">Note de la fiche</span>
            <textarea
              ref={noteInputRef}
              value={note}
              onChange={(event) => onNoteChange?.(event.target.value)}
              placeholder="Ex. Remarque du chauffeur, consigne opérationnelle ou rappel de livraison."
              rows={6}
              className="min-h-28 resize-y rounded-[4px] border border-slate-200 bg-white px-3 py-2 text-sm font-medium leading-5 text-slate-900 outline-none transition focus:border-border-accent-muted focus:ring-2 focus:ring-border-accent-subtle"
            />
          </label>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <ActionButton
              tone="secondary"
              size="sm"
              onClick={() => {
                onClose?.();
              }}
            >
              Annuler
            </ActionButton>
            <ActionButton tone="primary" size="sm" type="submit">
              {generateLabel}
            </ActionButton>
          </div>
        </form>
      </section>
    </div>
  );
}

function TruckPreloadReportModal({
  dispatchDate = "",
  employeeErrorMessage = "",
  employees = [],
  generateLabel = "Préparer le PDF",
  isLoadingEmployees = false,
  isOpen = false,
  note = "",
  onClose,
  onEmployeeChange,
  onGenerate,
  onNoteChange,
  onSelectedTruckIdsChange,
  selectedEmployeeId = "",
  selectedTruckIds = [],
  truckOptions = [],
}) {
  const employeeSelectRef = React.useRef(null);
  const noteInputRef = React.useRef(null);
  const normalizedSelectedTruckIds = React.useMemo(
    () => buildNormalizedIdList(selectedTruckIds),
    [selectedTruckIds],
  );
  const normalizedTruckOptionIds = React.useMemo(
    () => buildNormalizedIdList(truckOptions.map((truck) => truck?.value)),
    [truckOptions],
  );
  const selectedTruckCount = normalizedSelectedTruckIds.length;
  const areAllTrucksSelected = normalizedTruckOptionIds.length > 0 && selectedTruckCount === normalizedTruckOptionIds.length;

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => {
      if (employees.length > 0) {
        employeeSelectRef.current?.focus();
        return;
      }

      noteInputRef.current?.focus();
    });

    return () => {
      document.body.style.overflow = previousOverflow;
      window.cancelAnimationFrame(frame);
    };
  }, [employees.length, isOpen]);

  if (!isOpen) {
    return null;
  }

  const helperMessage = isLoadingEmployees
    ? "Chargement des employés du quart de soir..."
    : employeeErrorMessage
      ? employeeErrorMessage
      : employees.length > 0
        ? "Sélectionnez l'employé de soir à afficher en haut du PDF."
        : "Ajoutez un chauffeur au quart Soir dans Data Manager > Chauffeurs avant de générer ce rapport.";
  const truckSelectionMessage = truckOptions.length > 0
    ? `${selectedTruckCount} camion${selectedTruckCount > 1 ? "s" : ""} sélectionné${selectedTruckCount > 1 ? "s" : ""} sur ${truckOptions.length}.`
    : "Aucun camion en préchargement n'est disponible pour ce rapport.";
  const canSubmit = !isLoadingEmployees
    && !employeeErrorMessage
    && employees.length > 0
    && Boolean(selectedEmployeeId)
    && selectedTruckCount > 0;

  const handleToggleTruck = (truckId, isChecked) => {
    const normalizedTruckId = normalizeInlineText(truckId);
    if (!normalizedTruckId) {
      return;
    }

    const nextSelectedTruckIds = isChecked
      ? buildNormalizedIdList([...normalizedSelectedTruckIds, normalizedTruckId])
      : normalizedSelectedTruckIds.filter((selectedTruckId) => selectedTruckId !== normalizedTruckId);
    onSelectedTruckIdsChange?.(nextSelectedTruckIds);
  };

  const handleToggleAllTrucks = () => {
    onSelectedTruckIdsChange?.(areAllTrucksSelected ? [] : normalizedTruckOptionIds);
  };

  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center bg-slate-950/45 px-4 py-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <section
        aria-modal="true"
        className="w-full max-w-xl rounded-[4px] border border-black/6 bg-white p-4 shadow-[0_28px_80px_rgba(15,23,42,0.16)]"
        role="dialog"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="grid gap-1">
            <span className={sectionTagClassName}>PDF preload</span>
            <h3 className="text-[1.08rem] font-semibold tracking-[-0.03em] text-slate-950">Préparer le rapport Préchargements camions</h3>
            <p className="text-[0.82rem] leading-[1.4] text-slate-600">
              Le nom de l'employé sera imprimé dans l'en-tête du PDF, puis seuls les camions cochés apparaîtront dans le rapport.
            </p>
            <p className="text-[0.75rem] font-medium leading-5 text-slate-500">
              Préchargements camions
              {dispatchDate ? ` • ${dispatchDate}` : ""}
            </p>
          </div>

          <button
            type="button"
            className="rounded-[4px] border border-line-soft bg-surface-muted px-3 py-1 text-[0.86rem] font-semibold text-muted-strong transition hover:border-accent hover:bg-white hover:text-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            onClick={() => {
              onClose?.();
            }}
          >
            Fermer
          </button>
        </div>

        <form
          className="mt-3 grid gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit) {
              return;
            }
            onGenerate?.();
          }}
        >
          <div className="grid gap-3 min-[980px]:grid-cols-[minmax(0,1.2fr)_minmax(15rem,0.8fr)] min-[980px]:items-start">
            <div className="grid gap-3">
              <label className="grid gap-1.5 rounded-[4px] border border-[var(--shell-control-pastille-border,#e2e8f0)] bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.8))] px-3 py-2">
                <span className="text-[0.69rem] font-black uppercase tracking-[0.12em] text-slate-500">Employé de soir</span>
                <select
                  ref={employeeSelectRef}
                  value={selectedEmployeeId}
                  onChange={(event) => onEmployeeChange?.(event.target.value)}
                  disabled={isLoadingEmployees || employees.length <= 0}
                  className="rounded-[4px] border border-slate-200 bg-white px-3 py-2 text-sm font-medium leading-5 text-slate-900 outline-none transition focus:border-border-accent-muted focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                >
                  <option value="">Sélectionner un employé</option>
                  {employees.map((employee) => (
                    <option key={employee.employee_id} value={employee.employee_id}>
                      {employee.employee_name}
                    </option>
                  ))}
                </select>
                <p className={joinClasses([
                  "text-[0.75rem] leading-5",
                  employeeErrorMessage ? "text-danger" : "text-slate-500",
                ])}>
                  {helperMessage}
                </p>
              </label>

              <div className="grid gap-1.5 rounded-[4px] border border-[var(--shell-control-pastille-border,#e2e8f0)] bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.8))] px-3 py-2">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="grid gap-1">
                    <span className="text-[0.69rem] font-black uppercase tracking-[0.12em] text-slate-500">Camions à inclure</span>
                    <p className="text-[0.82rem] font-semibold leading-5 text-slate-900">{truckSelectionMessage}</p>
                  </div>

                  <button
                    type="button"
                    className="rounded-[4px] border border-slate-200 bg-white px-3 py-1 text-[0.75rem] font-semibold text-slate-600 transition hover:border-accent hover:text-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={truckOptions.length <= 0}
                    onClick={handleToggleAllTrucks}
                  >
                    {areAllTrucksSelected ? "Tout décocher" : "Tout cocher"}
                  </button>
                </div>

                {truckOptions.length > 0 ? (
                  <div className="grid max-h-44 gap-1 overflow-y-auto pr-1">
                    {truckOptions.map((truck) => {
                      const truckId = normalizeInlineText(truck?.value);
                      const truckLabel = normalizeInlineText(truck?.label) || truckId || "Camion";
                      const truckDetail = normalizeInlineText(truck?.detail);
                      const isChecked = normalizedSelectedTruckIds.includes(truckId);

                      return (
                        <label
                          key={truckId}
                          className="flex items-center gap-3 rounded-[4px] border border-slate-200 bg-white px-3 py-1.5 text-left transition hover:border-slate-300 hover:bg-slate-50"
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(event) => {
                              handleToggleTruck(truckId, event.target.checked);
                            }}
                            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-accent focus:ring-2 focus:ring-border-accent-subtle"
                          />
                          <span className="grid min-w-0 flex-1 gap-0.5">
                            <span className="whitespace-nowrap text-[0.94rem] font-semibold leading-5 text-slate-900">{truckLabel}</span>
                          </span>
                          {truckDetail ? (
                            <span className="shrink-0 text-[0.75rem] font-semibold leading-5 text-slate-500">{truckDetail}</span>
                          ) : null}
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="rounded-[4px] border border-dashed border-slate-300 bg-white px-3 py-2 text-[0.78rem] leading-5 text-slate-500">
                    Aucun camion en préchargement n'est disponible pour cette date.
                  </p>
                )}

                <p className="text-[0.75rem] leading-5 text-slate-500">
                  Les pages du PDF seront limitées aux camions cochés dans cette liste.
                </p>
              </div>
            </div>

            <label className="grid gap-1.5 rounded-[4px] border border-[var(--shell-control-pastille-border,#e2e8f0)] bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.8))] px-3 py-2 min-[980px]:h-full">
              <span className="text-[0.69rem] font-black uppercase tracking-[0.12em] text-slate-500">Note du rapport</span>
              <textarea
                ref={noteInputRef}
                value={note}
                onChange={(event) => onNoteChange?.(event.target.value)}
                placeholder="Ex. Valider le verrouillage final des compartiments avant impression."
                rows={9}
                className="min-h-40 resize-none rounded-[4px] border border-slate-200 bg-white px-3 py-2 text-sm font-medium leading-5 text-slate-900 outline-none transition focus:border-border-accent-muted focus:ring-2 focus:ring-border-accent-subtle min-[980px]:h-full"
              />
            </label>
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <ActionButton
              tone="secondary"
              size="sm"
              onClick={() => {
                onClose?.();
              }}
            >
              Annuler
            </ActionButton>
            <ActionButton tone="primary" size="sm" type="submit" disabled={!canSubmit}>
              {generateLabel}
            </ActionButton>
          </div>
        </form>
      </section>
    </div>
  );
}

export default function ReportsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { access, isReady: isAccessReady } = useAccess();
  const [dispatchDate, setDispatchDate] = React.useState(resolveInitialDispatchDate);
  const [dispatchDateInput, setDispatchDateInput] = React.useState(dispatchDate);
  React.useEffect(() => setDispatchDateInput(dispatchDate), [dispatchDate]);
  const [dispatchPeriod, setDispatchPeriod] = React.useState(resolveInitialDispatchPeriod);
  const [selectedMeunerieKey, setSelectedMeunerieKey] = React.useState("");
  const [meunerieTimeMode, setMeunerieTimeMode] = React.useState(MEUNERIE_TIME_MODE_DISPATCH);
  const [isMeunerieManualTimesOpen, setIsMeunerieManualTimesOpen] = React.useState(false);
  const [selectedTruckId, setSelectedTruckId] = React.useState(() => {
    if (typeof window === "undefined") {
      return "";
    }

    return readSelectedTruckIdFromSearch(window.location.search || "");
  });
  const [meunerieOptions, setMeunerieOptions] = React.useState([]);
  const [isOptionsLoading, setIsOptionsLoading] = React.useState(true);
  const [isEmailModalOpen, setIsEmailModalOpen] = React.useState(false);
  const [isSendingEmail, setIsSendingEmail] = React.useState(false);
  const [isTruckSheetEmailModalOpen, setIsTruckSheetEmailModalOpen] = React.useState(false);
  const [isSendingTruckSheetEmail, setIsSendingTruckSheetEmail] = React.useState(false);
  const [isTruckSheetEmailTemplateModalOpen, setIsTruckSheetEmailTemplateModalOpen] = React.useState(false);
  const [isLoadingTruckSheetEmailTemplate, setIsLoadingTruckSheetEmailTemplate] = React.useState(false);
  const [isSavingTruckSheetEmailTemplate, setIsSavingTruckSheetEmailTemplate] = React.useState(false);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [isTruckSheetNoteModalOpen, setIsTruckSheetNoteModalOpen] = React.useState(false);
  const [isTruckPreloadReportModalOpen, setIsTruckPreloadReportModalOpen] = React.useState(false);
  const [truckSheetPdfOrientation, setTruckSheetPdfOrientation] = React.useState("portrait");
  const [truckSheetPdfFitOnePage, setTruckSheetPdfFitOnePage] = React.useState(false);
  const [truckSheetPdfShowMap, setTruckSheetPdfShowMap] = React.useState(false);
  const [truckSheetPdfCompartmentsChoice, setTruckSheetPdfShowCompartments] = React.useState(true);
  const [emailForm, setEmailForm] = React.useState({
    recipientEmail: "",
    subject: "",
  });
  const [truckSheetEmailForm, setTruckSheetEmailForm] = React.useState({
    recipientEmail: "",
    subject: "",
    message: "",
  });
  const [truckSheetEmailTemplateForm, setTruckSheetEmailTemplateForm] = React.useState({
    subjectTemplate: "",
    messageTemplate: "",
  });
  const [emailModalError, setEmailModalError] = React.useState("");
  const [truckSheetEmailModalError, setTruckSheetEmailModalError] = React.useState("");
  const [truckSheetEmailTemplateError, setTruckSheetEmailTemplateError] = React.useState("");
  const [truckSheetEmailTemplate, setTruckSheetEmailTemplate] = React.useState({
    subjectTemplate: "",
    messageTemplate: "",
  });
  const [truckEmailContacts, setTruckEmailContacts] = React.useState([]);
  const [truckEmailContactsError, setTruckEmailContactsError] = React.useState("");
  const [truckSheetEmailFeedback, setTruckSheetEmailFeedback] = React.useState({
    message: "",
    tone: "neutral",
  });
  const [truckSheetPdfNote, setTruckSheetPdfNote] = React.useState("");
  // Last on-call dispatcher used in this browser; the PDF and email windows each prefill their own field with it.
  const [truckSheetOnCallDispatcher, setTruckSheetOnCallDispatcher] = React.useState(() => {
    try { return userUiPreferenceStorage("local")?.getItem(ON_CALL_DISPATCHER_STORAGE_KEY) || ""; } catch { return ""; }
  });
  const [truckSheetPdfOnCallDispatcher, setTruckSheetPdfOnCallDispatcher] = React.useState("");
  const storedDispatcher = useUserUiPreferenceStorageValue("local", ON_CALL_DISPATCHER_STORAGE_KEY);
  React.useEffect(() => { setTruckSheetOnCallDispatcher(storedDispatcher || ""); }, [storedDispatcher]);
  const rememberTruckSheetOnCallDispatcher = React.useCallback((value) => {
    setTruckSheetOnCallDispatcher(value);
    try { userUiPreferenceStorage("local")?.setItem(ON_CALL_DISPATCHER_STORAGE_KEY, value); } catch { /* storage unavailable */ }
  }, []);
  const [truckPreloadReportNote, setTruckPreloadReportNote] = React.useState("");
  const [truckPreloadCompartmentsChoice, setTruckPreloadShowCompartments] = React.useState(true);
  const [truckPreloadReportEmployeeId, setTruckPreloadReportEmployeeId] = React.useState("");
  const [truckPreloadReportSelectedTruckIds, setTruckPreloadReportSelectedTruckIds] = React.useState([]);
  const [isHistoryCollapsed, setIsHistoryCollapsed] = React.useState(true);
  const [historyItems, setHistoryItems] = React.useState([]);
  const [historyErrorMessage, setHistoryErrorMessage] = React.useState("");
  const [isHistoryLoading, setIsHistoryLoading] = React.useState(false);
  const [isHistoryRefreshing, setIsHistoryRefreshing] = React.useState(false);
  const [historyRevision, setHistoryRevision] = React.useState(0);
  const isHistoryFullPage = React.useMemo(
    () => new URLSearchParams(location.search).get("history") === "full",
    [location.search],
  );
  const refreshReportHistory = React.useCallback(() => {
    setHistoryRevision((currentValue) => currentValue + 1);
  }, []);
  const [truckPreloadNightEmployeeState, setTruckPreloadNightEmployeeState] = React.useState({
    employees: [],
    errorMessage: "",
    status: "idle",
  });
  const [optionsError, setOptionsError] = React.useState("");
  const [feedback, setFeedback] = React.useState({
    message: "",
    tone: "neutral",
  });
  const [preloadReportFeedback, setPreloadReportFeedback] = React.useState({
    message: "",
    tone: "neutral",
  });
  const [snapshotState, setSnapshotState] = React.useState({
    errorMessage: "",
    payload: null,
    status: dispatchDate ? "loading" : "idle",
  });
  const [isGeneratingTruckPreloadReport, setIsGeneratingTruckPreloadReport] = React.useState(false);
  const {
    downloadReadyResultsExport,
    exportState: resultsExportState,
    launchResultsExport: launchTrackedResultsExport,
    syncResultsExportFingerprint,
  } = useResultsExportTracker();
  const {
    exportState: fleetTruckSheetPdfExportState,
    launchTruckSheetPdfExport: launchFleetTruckSheetPdfExport,
    syncTruckSheetPdfFingerprint: syncFleetTruckSheetPdfFingerprint,
  } = useTruckSheetPdfExportTracker("fleet");
  const {
    exportState: selectedTruckSheetPdfExportState,
    launchTruckSheetPdfExport: launchSelectedTruckSheetPdfExport,
    syncTruckSheetPdfFingerprint: syncSelectedTruckSheetPdfFingerprint,
  } = useTruckSheetPdfExportTracker("selected");

  React.useEffect(() => {
    let isDisposed = false;
    const controller = typeof AbortController === "function" ? new AbortController() : null;

    const loadHistory = async () => {
      setHistoryErrorMessage("");
      setIsHistoryLoading(true);
      if (historyRevision > 0) {
        setIsHistoryRefreshing(true);
      }

      try {
        const payload = await getReportsActionsHistory({
          mode: isHistoryFullPage ? "full" : "preview",
          signal: controller?.signal,
        });
        if (isDisposed) {
          return;
        }
        setHistoryItems(Array.isArray(payload?.items) ? payload.items : []);
      } catch (error) {
        if (isDisposed || error?.name === "AbortError") {
          return;
        }
        setHistoryErrorMessage(resolveReportsHistoryErrorMessage(error));
      } finally {
        if (!isDisposed) {
          setIsHistoryLoading(false);
          setIsHistoryRefreshing(false);
        }
      }
    };

    void loadHistory();

    return () => {
      isDisposed = true;
      controller?.abort();
    };
  }, [historyRevision, isHistoryFullPage]);

  const handleOpenFullHistory = React.useCallback(() => {
    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.set("history", "full");
    navigate({
      pathname: location.pathname,
      search: `?${nextSearchParams.toString()}`,
    });
  }, [location.pathname, location.search, navigate]);

  const handleCloseFullHistory = React.useCallback(() => {
    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.delete("history");
    navigate({
      pathname: location.pathname,
      search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : "",
    });
  }, [location.pathname, location.search, navigate]);

  React.useEffect(() => {
    const searchDispatchDate = readDispatchDateFromSearch(location.search);
    if (!searchDispatchDate) {
      return;
    }

    setDispatchDate((currentValue) => (currentValue === searchDispatchDate ? currentValue : searchDispatchDate));
  }, [location.search]);

  React.useEffect(() => {
    const searchDispatchPeriod = readExplicitDispatchPeriodFromSearch(location.search);
    if (!searchDispatchPeriod) {
      return;
    }

    setDispatchPeriod((currentValue) => (currentValue === searchDispatchPeriod ? currentValue : searchDispatchPeriod));
  }, [location.search]);

  React.useEffect(() => {
    const searchSelectedTruckId = readSelectedTruckIdFromSearch(location.search);
    setSelectedTruckId((currentValue) => (currentValue === searchSelectedTruckId ? currentValue : searchSelectedTruckId));
  }, [location.search]);

  React.useEffect(() => {
    const currentDispatchDateInSearch = readDispatchDateFromSearch(location.search);
    if (currentDispatchDateInSearch === dispatchDate) {
      return;
    }

    const nextSearchParams = new URLSearchParams(location.search);
    if (dispatchDate) {
      nextSearchParams.set("dispatch_date", dispatchDate);
    } else {
      nextSearchParams.delete("dispatch_date");
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : "",
      },
      { replace: true },
    );
  }, [dispatchDate, location.pathname, location.search, navigate]);

  React.useEffect(() => {
    const currentSearchParams = new URLSearchParams(location.search);
    const currentDispatchPeriodInSearch = currentSearchParams.has("dispatch_period")
      ? readDispatchPeriodFromSearch(location.search)
      : "day";
    const normalizedDispatchPeriod = normalizeDispatchPeriod(dispatchPeriod);
    if (currentDispatchPeriodInSearch === normalizedDispatchPeriod && (normalizedDispatchPeriod === "evening" || !currentSearchParams.has("dispatch_period"))) {
      return;
    }

    const nextSearchParams = new URLSearchParams(location.search);
    if (normalizedDispatchPeriod === "evening") {
      nextSearchParams.set("dispatch_period", "evening");
    } else {
      nextSearchParams.delete("dispatch_period");
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : "",
      },
      { replace: true },
    );
  }, [dispatchPeriod, location.pathname, location.search, navigate]);

  React.useEffect(() => {
    const currentSelectedTruckIdInSearch = readSelectedTruckIdFromSearch(location.search);
    if (currentSelectedTruckIdInSearch === selectedTruckId) {
      return;
    }

    const nextSearchParams = new URLSearchParams(location.search);
    if (selectedTruckId) {
      nextSearchParams.set(REPORTS_SELECTED_TRUCK_QUERY_KEY, selectedTruckId);
    } else {
      nextSearchParams.delete(REPORTS_SELECTED_TRUCK_QUERY_KEY);
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, selectedTruckId]);

  React.useEffect(() => {
    if (!dispatchDate) {
      return;
    }

    // La Période choisie ici reste propre à Rapports : le lien Dispatch du menu ne doit pas l'hériter.
    writeLastDispatchContext({ dispatch_date: dispatchDate, dispatch_period: readLastDispatchContext().dispatch_period });
  }, [dispatchDate]);

  React.useEffect(() => {
    const abortController = new AbortController();

    setIsOptionsLoading(true);
    setOptionsError("");

    loadMeunerieReportOptions({ signal: abortController.signal })
      .then((items) => {
        if (abortController.signal.aborted) {
          return;
        }

        const resolvedItems = Array.isArray(items)
          ? items.filter((item) => (
            item
            && typeof item === "object"
            && typeof item.lookup_key === "string"
            && item.lookup_key.trim()
            && typeof item.label === "string"
            && item.label.trim()
          ))
          : [];

        setMeunerieOptions(resolvedItems);
        setSelectedMeunerieKey((currentValue) => (
          isAllMeuneriesSelection(currentValue) || resolvedItems.some((item) => item.lookup_key === currentValue)
            ? currentValue
            : ""
        ));
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
          return;
        }

        setMeunerieOptions([]);
        setOptionsError(
          error instanceof Error && error.message
            ? error.message
            : "Impossible de charger la liste des meuneries.",
        );
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsOptionsLoading(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, []);

  const canEmailReports = isAccessReady && canSendReportsEmail(access);
  React.useEffect(() => {
    setTruckEmailContactsError("");
    // Les courriels des chauffeurs ne servent qu'à l'envoi par courriel (reports.email.send).
    if (!canEmailReports) {
      return undefined;
    }
    const abortController = new AbortController();

    loadTruckSheetEmailContacts({ dispatchDate, signal: abortController.signal })
      .then((items) => {
        if (abortController.signal.aborted) {
          return;
        }

        const resolvedItems = Array.isArray(items)
          ? items.filter((item) => (
            item
            && typeof item === "object"
            && typeof item.truck_id === "string"
            && item.truck_id.trim()
          ))
          : [];

        setTruckEmailContacts(resolvedItems);
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
          return;
        }

        setTruckEmailContacts([]);
        setTruckEmailContactsError(
          error instanceof Error && error.message
            ? error.message
            : "Impossible de charger les courriels des camions.",
        );
      });

    return () => {
      abortController.abort();
    };
  }, [canEmailReports, dispatchDate]);

  React.useEffect(() => {
    if (!dispatchDate) {
      setSnapshotState({
        errorMessage: "",
        payload: null,
        status: "idle",
      });
      return;
    }

    const abortController = new AbortController();
    setSnapshotState({
      errorMessage: "",
      payload: null,
      status: "loading",
    });

    const searchParams = new URLSearchParams();
    searchParams.set("dispatch_date", dispatchDate);
    searchParams.set("include_dispatch_bootstrap", "1");
    if (normalizeDispatchPeriod(dispatchPeriod) === "evening") {
      searchParams.set("dispatch_period", "evening");
    }

    fetchWithSupabaseAuth(`/api/results/latest?${searchParams.toString()}`, {
      signal: abortController.signal,
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => null);

        if (!response.ok && response.status !== 404) {
          throw new Error(resolveResultsSnapshotRequestErrorMessage(response, payload));
        }

        if (abortController.signal.aborted) {
          return;
        }

        setSnapshotState({
          errorMessage: "",
          payload: payload && typeof payload === "object" ? payload : {},
          status: "ready",
        });
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
          return;
        }

        setSnapshotState({
          errorMessage: error instanceof Error && error.message
            ? error.message
            : "Impossible de charger le dispatch lié à cette date.",
          payload: null,
          status: "error",
        });
      });

    return () => {
      abortController.abort();
    };
  }, [dispatchDate, dispatchPeriod]);

  const isAllMeuneriesSelected = isAllMeuneriesSelection(selectedMeunerieKey);
  const canChooseTruckSheetCompartments = isAccessReady && hasPermission(access, ACCESS_PERMISSION_KEYS.reportsTruckSheetsCompartmentsShow);
  const canChooseTruckPreloadCompartments = isAccessReady && hasPermission(access, ACCESS_PERMISSION_KEYS.reportsTruckPreloadsCompartmentsShow);
  const truckSheetPdfShowCompartments = canChooseTruckSheetCompartments ? truckSheetPdfCompartmentsChoice : true;
  const truckPreloadShowCompartments = canChooseTruckPreloadCompartments ? truckPreloadCompartmentsChoice : true;
  React.useEffect(() => {
    if (!canChooseTruckSheetCompartments) setTruckSheetPdfShowCompartments(true);
    if (!canChooseTruckPreloadCompartments) setTruckPreloadShowCompartments(true);
  }, [canChooseTruckSheetCompartments, canChooseTruckPreloadCompartments]);
  const reportEmailDate = dispatchDate || null;
  const canEditTruckSheetEmailTemplate = isAccessReady
    && hasPermission(access, ACCESS_PERMISSION_KEYS.reportsEmailTemplateWrite);
  const truckSheetActionCount = 1
    + Number(canEmailReports)
    + Number(canEmailReports && canManageAutomations(access))
    + Number(canEditTruckSheetEmailTemplate);

  React.useEffect(() => {
    if (!isAccessReady || !canEmailReports) {
      return undefined;
    }

    const abortController = new AbortController();
    setIsLoadingTruckSheetEmailTemplate(true);
    loadTruckSheetEmailTemplate({ signal: abortController.signal })
      .then((payload) => {
        if (abortController.signal.aborted) {
          return;
        }

        const subjectTemplate = typeof payload?.subject_template === "string" && payload.subject_template.trim()
          ? payload.subject_template
          : DEFAULT_TRUCK_SHEET_EMAIL_SUBJECT_TEMPLATE;
        const messageTemplate = typeof payload?.message_template === "string" && payload.message_template.trim()
          ? payload.message_template
          : DEFAULT_TRUCK_SHEET_EMAIL_MESSAGE_TEMPLATE;
        setTruckSheetEmailTemplate({ subjectTemplate, messageTemplate });
        setTruckSheetEmailTemplateForm({ subjectTemplate, messageTemplate });
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
          return;
        }

        setTruckSheetEmailTemplate({
          subjectTemplate: DEFAULT_TRUCK_SHEET_EMAIL_SUBJECT_TEMPLATE,
          messageTemplate: DEFAULT_TRUCK_SHEET_EMAIL_MESSAGE_TEMPLATE,
        });
        setTruckSheetEmailTemplateForm({
          subjectTemplate: DEFAULT_TRUCK_SHEET_EMAIL_SUBJECT_TEMPLATE,
          messageTemplate: DEFAULT_TRUCK_SHEET_EMAIL_MESSAGE_TEMPLATE,
        });
        setTruckSheetEmailTemplateError(
          error instanceof Error && error.message
            ? error.message
            : "Impossible de charger le modèle de courriel.",
        );
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsLoadingTruckSheetEmailTemplate(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [canEmailReports, isAccessReady]);
  const meunerieSelectOptions = React.useMemo(() => {
    if (meunerieOptions.length === 0) {
      return [];
    }

    return [ALL_MEUNERIES_OPTION, ...meunerieOptions];
  }, [meunerieOptions]);
  const selectedMeunerie = isAllMeuneriesSelected
    ? ALL_MEUNERIES_OPTION
    : meunerieOptions.find((item) => item.lookup_key === selectedMeunerieKey) || null;
  const selectedMeunerieLabel = selectedMeunerie?.label || "Meunerie sélectionnée";
  const canGenerateReport = Boolean(dispatchDate && selectedMeunerieKey) && !isGenerating && !isOptionsLoading;
  const snapshotPayload = snapshotState.payload && typeof snapshotState.payload === "object"
    ? snapshotState.payload
    : null;
  const assignmentList = React.useMemo(() => {
    if (!snapshotPayload || isMissingLatestOptimizationSnapshot(snapshotPayload)) {
      return [];
    }

    const workspaceState = applyResultsDataToState({ openTruckIds: new Set() }, snapshotPayload);
    return Array.isArray(workspaceState.assignmentList) ? workspaceState.assignmentList : [];
  }, [snapshotPayload]);

  const truckOptions = React.useMemo(() => {
    if (assignmentList.length === 0) {
      return [];
    }

    return assignmentList
      .map((assignment) => {
        const truckId = normalizeInlineText(assignment?.truck_id);
        if (!truckId) {
          return null;
        }

        const truckLabel = normalizeInlineText(assignment?.truck_name) || truckId;
        const trailerLabel = normalizeInlineText(assignment?.compartment_name);
        const tractorLabel = normalizeInlineText(assignment?.day_truck_name) || (trailerLabel && truckLabel.endsWith(`-${trailerLabel}`)
          ? truckLabel.slice(0, -(trailerLabel.length + 1)) : truckLabel);
        const driverName = normalizeInlineText(assignment?.driver_name);
        const trips = Array.isArray(assignment?.trips) ? assignment.trips.filter(Boolean) : [];
        // Même critère que le PDF chauffeur : une mission exploitable dans l'affectation.
        const hasSheetContent = (Array.isArray(assignment?.operational_steps) && assignment.operational_steps.length > 0)
          || trips.some((trip) => (
            (Array.isArray(trip?.operational_steps) && trip.operational_steps.length > 0)
            || (Array.isArray(trip?.stops) && trip.stops.length > 0)
          ));
        const deliveryCount = trips.reduce((sum, trip) => {
          const nextValue = Number(trip?.delivery_count);
          return Number.isFinite(nextValue) ? sum + nextValue : sum;
        }, 0);

        return {
          detail: trips.length > 0
            ? `${deliveryCount} livraison${deliveryCount > 1 ? "s" : ""}`
            : "Aucune livraison active",
          driverName,
          hasSheetContent,
          label: truckLabel,
          truckLabel: tractorLabel,
          trailerLabel,
          value: truckId,
        };
      })
      .filter(Boolean);
    }, [assignmentList, dispatchPeriod]);
  const driverOptions = React.useMemo(
    () => truckOptions
      .filter((option) => option.driverName && option.hasSheetContent)
      .map((option) => ({ ...option, label: option.driverName }))
      .sort((left, right) => left.label.localeCompare(right.label, "fr-CA", { sensitivity: "base" })),
    [truckOptions],
  );
  const truckEmailById = React.useMemo(() => {
    const emailMap = new Map();
    const isEveningPeriod = normalizeDispatchPeriod(dispatchPeriod) === "evening";

    truckEmailContacts.forEach((contact) => {
      const truckId = normalizeInlineText(contact?.truck_id);
      if (!truckId) {
        return;
      }

      emailMap.set(
        truckId,
        normalizeInlineText(isEveningPeriod ? contact?.evening_courriel : contact?.courriel),
      );
    });

    return emailMap;
  }, [dispatchPeriod, truckEmailContacts]);
  const preloadTruckOptions = React.useMemo(() => {
    if (assignmentList.length === 0 || truckOptions.length === 0) {
      return [];
    }

    const preloadTruckIds = new Set(
      assignmentList
        .filter((assignment) => resolveAssignmentHasPreloadedPreviousDay(assignment))
        .map((assignment) => normalizeInlineText(assignment?.truck_id))
        .filter(Boolean),
    );
    return truckOptions.filter((option) => preloadTruckIds.has(option.value));
  }, [assignmentList, truckOptions]);
  const preloadTruckCount = preloadTruckOptions.length;
  const isAllTrucksSelected = isAllTrucksSelection(selectedTruckId);
  const effectiveSelectedTruckId = isAllTrucksSelected ? "" : selectedTruckId;
  const truckSelectOptions = React.useMemo(() => {
    if (driverOptions.length === 0) {
      return [];
    }

    return [ALL_TRUCKS_OPTION, ...driverOptions];
  }, [driverOptions]);
  const selectedTruck = isAllTrucksSelected
    ? ALL_TRUCKS_OPTION
    : driverOptions.find((option) => option.value === selectedTruckId) || null;
  const selectedTruckCourriel = isAllTrucksSelected
    ? ""
    : truckEmailById.get(effectiveSelectedTruckId) || "";
  const selectedTruckPdfReadyRoute = React.useMemo(
    () => appendDispatchDateToUrl(buildSelectedTruckPdfReadyRoute(effectiveSelectedTruckId, dispatchPeriod), dispatchDate, dispatchPeriod),
    [dispatchDate, dispatchPeriod, effectiveSelectedTruckId],
  );
  const fleetTruckPdfReadyRoute = React.useMemo(
    () => appendDispatchDateToUrl(REPORTS_FLEET_PDF_READY_ROUTE, dispatchDate, dispatchPeriod),
    [dispatchDate, dispatchPeriod],
  );
  const exportContext = React.useMemo(() => buildReportsExportContext(snapshotPayload, {
    dispatchPeriod,
    selectedTruckId: effectiveSelectedTruckId,
    selectedTruckLabel: selectedTruck?.label || "",
  }), [dispatchPeriod, effectiveSelectedTruckId, selectedTruck?.label, snapshotPayload]);
  const snapshotMissing = Boolean(snapshotPayload) && isMissingLatestOptimizationSnapshot(snapshotPayload);
  const hasSnapshot = exportContext.hasSnapshot;
  const isSnapshotLoading = snapshotState.status === "loading";
  const snapshotLoadError = snapshotState.status === "error" ? snapshotState.errorMessage : "";
  const resultsExportFingerprint = exportContext.resultsExport.fingerprint;
  const fleetTruckSheetPdfFingerprint = exportContext.truckSheetPdf.fleetFingerprint;
  const selectedTruckSheetPdfFingerprint = exportContext.truckSheetPdf.selectedFingerprint;
  const isResultsExportBusy = resultsExportState.status === "launching" || resultsExportState.status === "pending_async";
  const isResultsExportReady = Boolean(resultsExportFingerprint)
    && resultsExportState.status === "ready_async"
    && resultsExportState.fingerprint === resultsExportFingerprint;
  const isFleetTruckSheetPdfBusy = fleetTruckSheetPdfExportState.status === "launching"
    || fleetTruckSheetPdfExportState.status === "pending_async";
  const isSelectedTruckSheetPdfBusy = selectedTruckSheetPdfExportState.status === "launching"
    || selectedTruckSheetPdfExportState.status === "pending_async";
  const isTruckSheetPdfBusy = isAllTrucksSelected ? isFleetTruckSheetPdfBusy : isSelectedTruckSheetPdfBusy;
  const isResultsExportBlocked = exportContext.resultsExport.snapshotStatus === "stale"
    && !exportContext.allowStaleSnapshotActions;
  const isTruckSheetSnapshotBlocked = exportContext.truckSheetPdf.snapshotStatus === "stale"
    && !exportContext.allowStaleSnapshotActions;
  const isFleetTruckSheetPdfBlocked = isTruckSheetSnapshotBlocked;
  const isSelectedTruckSheetPdfBlocked = isTruckSheetSnapshotBlocked;
  const isTruckSheetPdfBlocked = isTruckSheetSnapshotBlocked;
  const truckSheetPdfBlockedReason = exportContext.truckSheetPdf.blockedReason;
  const isTruckPreloadEmployeesLoading = truckPreloadNightEmployeeState.status === "loading";

  React.useEffect(() => {
    // Pendant le chargement d'une autre date, la liste est vide : effacer le camion ici
    // réécrivait l'URL avec l'ancienne date et faisait osciller la Date de dispatch.
    if (!selectedTruckId || isSnapshotLoading) {
      return;
    }

    if (isAllTrucksSelection(selectedTruckId) && driverOptions.length > 0) {
      return;
    }

    if (driverOptions.some((option) => option.value === selectedTruckId)) {
      return;
    }

    setSelectedTruckId("");
  }, [driverOptions, isSnapshotLoading, selectedTruckId]);

    React.useEffect(() => {
      const allowedTruckIds = new Set(preloadTruckOptions.map((option) => option.value));
      setTruckPreloadReportSelectedTruckIds((currentValue) => {
        const normalizedCurrentValue = buildNormalizedIdList(currentValue);
        const filteredTruckIds = normalizedCurrentValue.filter((truckId) => allowedTruckIds.has(truckId));
        return filteredTruckIds.length === normalizedCurrentValue.length
          && filteredTruckIds.every((truckId, index) => truckId === normalizedCurrentValue[index])
          ? currentValue
          : filteredTruckIds;
      });
    }, [preloadTruckOptions]);

  React.useEffect(() => {
    syncResultsExportFingerprint(resultsExportFingerprint, {
      resultUrl: REPORTS_RESULTS_EXPORT_READY_ROUTE,
    });
  }, [resultsExportFingerprint, syncResultsExportFingerprint]);

  React.useEffect(() => {
    syncFleetTruckSheetPdfFingerprint("");
    syncSelectedTruckSheetPdfFingerprint("");
  }, [syncFleetTruckSheetPdfFingerprint, syncSelectedTruckSheetPdfFingerprint]);

  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (
      searchParams.get("downloadExport") !== "1"
      || resultsExportState.status !== "ready_async"
      || (resultsExportFingerprint && resultsExportState.fingerprint !== resultsExportFingerprint)
    ) {
      return undefined;
    }

    let isDisposed = false;

    const runDownload = async () => {
      await downloadReadyResultsExport();
      if (isDisposed) {
        return;
      }

      const nextSearchParams = new URLSearchParams(location.search);
      nextSearchParams.delete("downloadExport");
      navigate(
        {
          pathname: location.pathname,
          search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : "",
        },
        { replace: true },
      );
    };

    void runDownload();

    return () => {
      isDisposed = true;
    };
  }, [downloadReadyResultsExport, location.pathname, location.search, navigate, resultsExportFingerprint, resultsExportState.fingerprint, resultsExportState.status]);

  const handleGenerateReport = React.useCallback(async () => {
    if (!dispatchDate) {
      setFeedback({
        message: "Sélectionnez une date de dispatch avant de générer le rapport.",
        tone: "danger",
      });
      return;
    }

    if (!selectedMeunerieKey) {
      setFeedback({
        message: "Sélectionnez une meunerie avant de générer le rapport.",
        tone: "danger",
      });
      return;
    }

    let documentWindow = null;
    setIsGenerating(true);
    setFeedback({ message: "", tone: "neutral" });

    try {
      documentWindow = openExportDocumentWindow({
        detail: `${selectedMeunerie?.label || "Meunerie"} • ${dispatchDate}`,
        eyebrow: "Rapport Meunerie",
        message: isAllMeuneriesSelected
          ? "Nous préparons un PDF groupé avec une page dédiée pour chaque meunerie active à la date demandée."
          : "Nous préparons le PDF de la meunerie sélectionnée pour la date demandée.",
        preferredWidth: 1240,
        title: isAllMeuneriesSelected ? "Préparation des rapports Meunerie" : "Préparation du rapport Meunerie",
        windowName: "grain-meunerie-report",
      });

      await downloadMeunerieReport({
        dispatchDate,
        documentWindow,
        meunerieLookupKey: selectedMeunerieKey,
        timeMode: meunerieTimeMode,
      });

      refreshReportHistory();
      setFeedback({
        message: isAllMeuneriesSelected
          ? "Les rapports sont prêts et ont été envoyés vers le navigateur dans un PDF groupé."
          : "Le rapport est prêt et a été envoyé vers le navigateur.",
        tone: "success",
      });
    } catch (error) {
      const errorMessage = isExportApiError(error)
        ? error.message
        : error instanceof Error && error.message
          ? error.message
          : "Le rapport Meunerie n'a pas pu être généré.";
      if (documentWindow) {
        markExportDocumentWindowFailed(documentWindow, {
          detail: errorMessage,
          eyebrow: "Rapport Meunerie",
          message: "Le rapport Meunerie n'a pas pu être généré.",
          title: "Export interrompu",
        });
      }

      setFeedback({
        message: errorMessage,
        tone: "danger",
      });
    } finally {
      setIsGenerating(false);
    }
  }, [dispatchDate, isAllMeuneriesSelected, meunerieTimeMode, refreshReportHistory, selectedMeunerie?.label, selectedMeunerieKey]);

  const handleResultsExportAction = React.useCallback(() => {
    if (!dispatchDate || !hasSnapshot) {
      return;
    }

    if (isResultsExportReady) {
      void downloadReadyResultsExport({
        openDocumentWindow: true,
        promptForSaveLocation: true,
      }).finally(() => {
        refreshReportHistory();
      });
      return;
    }

    void launchTrackedResultsExport({
      blocked: isResultsExportBlocked,
      blockedReason: exportContext.resultsExport.blockedReason,
      fingerprint: resultsExportFingerprint,
      openDocumentWindow: true,
      promptForSaveLocation: true,
      resultUrl: REPORTS_RESULTS_EXPORT_READY_ROUTE,
    }).finally(() => {
      refreshReportHistory();
    });
  }, [dispatchDate, downloadReadyResultsExport, exportContext.resultsExport.blockedReason, hasSnapshot, isResultsExportBlocked, isResultsExportReady, launchTrackedResultsExport, refreshReportHistory, resultsExportFingerprint]);

  const handleFleetTruckSheetPdfAction = React.useCallback((notes = "", onCallDispatcher = "") => {
    if (!dispatchDate || !hasSnapshot) {
      return;
    }

    const normalizedNotes = normalizeInlineText(notes);

    void launchFleetTruckSheetPdfExport({
      blocked: isFleetTruckSheetPdfBlocked,
      blockedReason: truckSheetPdfBlockedReason,
      dispatchPeriod,
      fingerprint: buildTruckSheetPdfExportFingerprint(fleetTruckSheetPdfFingerprint, {
        dispatchPeriod,
        exportMode: "combined",
        fitOnePage: truckSheetPdfFitOnePage,
        generationNonce: String(Date.now()),
        notes: normalizedNotes,
        orientation: truckSheetPdfOrientation,
        showCompartments: truckSheetPdfShowCompartments,
        showMap: truckSheetPdfShowMap,
        truckIds: [],
      }),
      openDocumentWindow: true,
      resultUrl: fleetTruckPdfReadyRoute,
      fitOnePage: truckSheetPdfFitOnePage,
      notes: normalizedNotes,
      onCallDispatcher,
      orientation: truckSheetPdfOrientation,
      showCompartments: truckSheetPdfShowCompartments,
      showMap: truckSheetPdfShowMap,
      truckIds: [],
    }).finally(() => {
      refreshReportHistory();
    });
  }, [dispatchDate, dispatchPeriod, fleetTruckPdfReadyRoute, fleetTruckSheetPdfFingerprint, hasSnapshot, isFleetTruckSheetPdfBlocked, launchFleetTruckSheetPdfExport, refreshReportHistory, truckSheetPdfBlockedReason, truckSheetPdfFitOnePage, truckSheetPdfOrientation, truckSheetPdfShowCompartments, truckSheetPdfShowMap]);

  const handleSelectedTruckSheetPdfAction = React.useCallback((notes = "", onCallDispatcher = "") => {
    if (!dispatchDate || !hasSnapshot || !effectiveSelectedTruckId) {
      return;
    }

    const normalizedNotes = normalizeInlineText(notes);

    void launchSelectedTruckSheetPdfExport({
      blocked: isSelectedTruckSheetPdfBlocked,
      blockedReason: truckSheetPdfBlockedReason,
      dispatchPeriod,
      fingerprint: buildTruckSheetPdfExportFingerprint(selectedTruckSheetPdfFingerprint, {
        dispatchPeriod,
        exportMode: "single",
        fitOnePage: truckSheetPdfFitOnePage,
        generationNonce: String(Date.now()),
        notes: normalizedNotes,
        orientation: truckSheetPdfOrientation,
        showCompartments: truckSheetPdfShowCompartments,
        showMap: truckSheetPdfShowMap,
        truckIds: [effectiveSelectedTruckId],
      }),
      openDocumentWindow: true,
      resultUrl: selectedTruckPdfReadyRoute,
      fitOnePage: truckSheetPdfFitOnePage,
      notes: normalizedNotes,
      onCallDispatcher,
      orientation: truckSheetPdfOrientation,
      showCompartments: truckSheetPdfShowCompartments,
      showMap: truckSheetPdfShowMap,
      truckIds: [effectiveSelectedTruckId],
    }).finally(() => {
      refreshReportHistory();
    });
  }, [dispatchDate, dispatchPeriod, effectiveSelectedTruckId, hasSnapshot, isSelectedTruckSheetPdfBlocked, launchSelectedTruckSheetPdfExport, refreshReportHistory, selectedTruckPdfReadyRoute, selectedTruckSheetPdfFingerprint, truckSheetPdfBlockedReason, truckSheetPdfFitOnePage, truckSheetPdfOrientation, truckSheetPdfShowCompartments, truckSheetPdfShowMap]);

  const handleTruckSheetPdfAction = React.useCallback(() => {
    setTruckSheetPdfNote("");
    setTruckSheetPdfOnCallDispatcher(truckSheetOnCallDispatcher);
    setIsTruckSheetNoteModalOpen(true);
  }, [truckSheetOnCallDispatcher]);

  const handleCloseTruckSheetNoteModal = React.useCallback(() => {
    setIsTruckSheetNoteModalOpen(false);
  }, []);

  const handleConfirmTruckSheetNoteModal = React.useCallback(() => {
    const normalizedNote = normalizeInlineText(truckSheetPdfNote);
    const onCallDispatcher = normalizeInlineText(truckSheetPdfOnCallDispatcher);
    rememberTruckSheetOnCallDispatcher(onCallDispatcher);
    if (isAllTrucksSelected) {
      handleFleetTruckSheetPdfAction(normalizedNote, onCallDispatcher);
    } else {
      handleSelectedTruckSheetPdfAction(normalizedNote, onCallDispatcher);
    }
    setIsTruckSheetNoteModalOpen(false);
  }, [handleFleetTruckSheetPdfAction, handleSelectedTruckSheetPdfAction, isAllTrucksSelected, rememberTruckSheetOnCallDispatcher, truckSheetPdfNote, truckSheetPdfOnCallDispatcher]);

  const handleOpenTruckPreloadReportModal = React.useCallback(() => {
    if (!dispatchDate) {
      setPreloadReportFeedback({
        message: "Sélectionnez une date de dispatch avant de générer le rapport.",
        tone: "danger",
      });
      return;
    }

    if (!hasSnapshot) {
      setPreloadReportFeedback({
        message: "Aucun dispatch calculé n'est disponible pour cette date.",
        tone: "danger",
      });
      return;
    }

    if (preloadTruckCount <= 0) {
      setPreloadReportFeedback({
        message: "Aucun camion avec commande préchargée la veille n'est disponible pour cette date.",
        tone: "danger",
      });
      return;
    }

    setPreloadReportFeedback({ message: "", tone: "neutral" });
    setTruckPreloadReportNote("");
    setTruckPreloadReportEmployeeId("");
    setTruckPreloadReportSelectedTruckIds(preloadTruckOptions.map((option) => option.value));
    setTruckPreloadNightEmployeeState((currentValue) => ({
      employees: Array.isArray(currentValue.employees) ? currentValue.employees : [],
      errorMessage: "",
      status: "loading",
    }));
    setIsTruckPreloadReportModalOpen(true);

    void (async () => {
      try {
        const payload = await fetchDataManagerState({ sections: ["employees"] });
        const nightEmployees = Array.isArray(payload?.employees)
          ? [...payload.employees]
            .filter((employee) => isNightEmployeeShift(employee?.shift))
            .sort((leftEmployee, rightEmployee) => normalizeInlineText(leftEmployee?.employee_name).localeCompare(
              normalizeInlineText(rightEmployee?.employee_name),
              "fr-CA",
              { sensitivity: "base" },
            ))
          : [];

        setTruckPreloadNightEmployeeState({
          employees: nightEmployees,
          errorMessage: "",
          status: "ready",
        });
        setTruckPreloadReportEmployeeId(nightEmployees[0]?.employee_id || "");
      } catch (error) {
        const errorMessage = error instanceof Error && error.message
          ? error.message
          : "Impossible de charger les employés de soir.";
        setTruckPreloadNightEmployeeState({
          employees: [],
          errorMessage,
          status: "error",
        });
        setTruckPreloadReportEmployeeId("");
      }
    })();
  }, [dispatchDate, hasSnapshot, preloadTruckCount, preloadTruckOptions]);

  const handleCloseTruckPreloadReportModal = React.useCallback(() => {
    setIsTruckPreloadReportModalOpen(false);
  }, []);

  const dispatchPeriodLabel = normalizeDispatchPeriod(dispatchPeriod) === "evening" ? "Soir" : "Jour";
  const truckSheetPdfActionLabel = "Générer le PDF";
  const canGenerateTruckPreloadReport = Boolean(dispatchDate && hasSnapshot && preloadTruckCount > 0) && !isGeneratingTruckPreloadReport;

  const dispatchExportUnavailableReason = !dispatchDate
    ? "Sélectionnez une date de dispatch pour activer cet export."
    : isSnapshotLoading
      ? "Chargement du dernier dispatch calculé pour cette date."
      : snapshotLoadError
        ? snapshotLoadError
        : "Aucun dispatch calculé n'est disponible pour cette date.";
  const resultsExportNote = !dispatchDate || !hasSnapshot
    ? dispatchExportUnavailableReason
    : isResultsExportBusy
      ? "Préparation du fichier XLSX en arrière-plan."
      : isResultsExportReady
        ? "Le fichier XLSX est prêt au téléchargement."
        : isResultsExportBlocked
          ? "Relancez l'optimisation pour réactiver cet export."
          : exportContext.sourceStaleNotice
            ? "Exporter le dernier dispatch calculé visible au format XLSX."
            : "Exporter les résultats détaillés au format XLSX."
  ;
  const meunerieEmailDisabledReason = !dispatchDate
    ? "Sélectionnez une date de dispatch avant d'envoyer le rapport par courriel."
    : !selectedMeunerieKey
      ? "Sélectionnez une meunerie avant d'envoyer le rapport par courriel."
      : isOptionsLoading
        ? "Chargement des meuneries en cours."
        : isSnapshotLoading
          ? "Chargement du dernier dispatch calculé pour cette date."
          : snapshotLoadError
            ? snapshotLoadError
            : !hasSnapshot
              ? "Aucun dispatch calculé n'est disponible pour cette date."
              : "";
  const canOpenMeunerieEmailModal = canEmailReports && !meunerieEmailDisabledReason;
  const truckSheetEmailDisabledReason = !dispatchDate
    ? "Sélectionnez une date de dispatch avant d'envoyer l'horaire chauffeur par courriel."
    : isAllTrucksSelected
      ? "Sélectionnez un chauffeur précis avant d'envoyer l'horaire chauffeur par courriel."
      : !effectiveSelectedTruckId
        ? "Sélectionnez un chauffeur avant d'envoyer l'horaire chauffeur par courriel."
        : !normalizeInlineText(selectedTruck?.driverName)
          ? "Aucun chauffeur n'est associé à ce camion pour cette période. Corrigez l'affectation avant l'envoi."
        : isSnapshotLoading
          ? "Chargement du dernier dispatch calculé pour cette date."
          : snapshotLoadError
            ? snapshotLoadError
            : !hasSnapshot
              ? "Aucun dispatch calculé n'est disponible pour cette date."
              : isTruckSheetPdfBlocked
                ? exportContext.truckSheetPdf.blockedReason
                : "";
  const canOpenTruckSheetEmailModal = canEmailReports && !truckSheetEmailDisabledReason;
  const meunerieGenerateDisabledReason = isGenerating
    ? ""
    : isOptionsLoading
      ? "Chargement des meuneries en cours."
      : !dispatchDate
        ? "Sélectionnez une date de dispatch pour générer le rapport."
        : meunerieOptions.length === 0
          ? "Aucune meunerie active n'est disponible pour ce rapport."
          : !selectedMeunerieKey
            ? "Sélectionnez une meunerie avant de générer le PDF."
            : "";
  const meunerieManualTimesDisabledReason = isOptionsLoading
    ? "Chargement des meuneries en cours."
    : !dispatchDate
      ? "Sélectionnez une date de dispatch pour saisir les heures."
      : !selectedMeunerieKey
        ? "Sélectionnez une meunerie avant de saisir les heures."
        : "";
  const meunerieEmailActionDisabledReason = isEmailModalOpen
    ? "Une fenêtre d'envoi est déjà ouverte."
    : meunerieEmailDisabledReason;
  const truckSheetPrepareDisabledReason = isTruckSheetPdfBusy
    ? ""
    : !dispatchDate
      ? "Sélectionnez une date de dispatch pour préparer l'horaire."
      : isSnapshotLoading
        ? "Chargement du dernier dispatch calculé pour cette date."
        : snapshotLoadError
          ? snapshotLoadError
          : !hasSnapshot
            ? "Aucun dispatch calculé n'est disponible pour cette date."
            : !selectedTruckId
              ? "Sélectionnez un chauffeur, ou tous les chauffeurs, avant de préparer l'horaire."
              : isTruckSheetPdfBlocked
                ? truckSheetPdfBlockedReason || "Le snapshot du dispatch est périmé. Relancez l'optimisation pour réactiver cet export."
                : "";
  const truckSheetEmailActionDisabledReason = isTruckSheetPdfBusy
    ? "Attendez la fin de la préparation de l'horaire avant d'envoyer le courriel."
    : isTruckSheetEmailModalOpen
      ? "Une fenêtre d'envoi est déjà ouverte."
      : truckSheetEmailDisabledReason;
  const truckSheetTemplateDisabledReason = isLoadingTruckSheetEmailTemplate
    ? "Chargement du modèle de courriel."
    : isTruckSheetEmailTemplateModalOpen
      ? "La fenêtre de personnalisation est déjà ouverte."
      : isTruckSheetPdfBusy
        ? "Attendez la fin de la préparation de l'horaire avant de modifier le modèle."
        : "";
  const truckPreloadGenerateDisabledReason = isGeneratingTruckPreloadReport
    ? ""
    : !dispatchDate || isSnapshotLoading || !hasSnapshot
      ? dispatchExportUnavailableReason
      : preloadTruckCount <= 0
        ? "Aucun camion avec commande préchargée la veille n'est disponible dans le snapshot courant."
        : "";
  const resultsExportDisabledReason = isResultsExportBusy
    ? ""
    : !dispatchDate || !hasSnapshot
      ? dispatchExportUnavailableReason
      : "";

  const handleOpenMeunerieEmailModal = React.useCallback(() => {
    if (!canOpenMeunerieEmailModal) {
      return;
    }

    setEmailForm({
      recipientEmail: "",
      subject: buildDefaultMeunerieEmailSubject({
        dispatchDate,
        meunerieLabel: selectedMeunerieLabel,
      }),
    });
    setEmailModalError("");
    setFeedback({ message: "", tone: "neutral" });
    setIsEmailModalOpen(true);
  }, [canOpenMeunerieEmailModal, dispatchDate, selectedMeunerieLabel]);

  const handleCloseMeunerieEmailModal = React.useCallback(() => {
    if (isSendingEmail) {
      return;
    }

    setEmailModalError("");
    setIsEmailModalOpen(false);
  }, [isSendingEmail]);

  const handleSendMeunerieEmail = React.useCallback(async () => {
    if (isSendingEmail) {
      return;
    }

    const recipientEmail = normalizeInlineText(emailForm.recipientEmail);
    const subject = normalizeInlineText(emailForm.subject);

    if (meunerieEmailDisabledReason) {
      setEmailModalError(meunerieEmailDisabledReason);
      return;
    }

    if (!isValidEmailAddress(recipientEmail)) {
      setEmailModalError("Saisissez une adresse courriel destinataire valide.");
      return;
    }

    if (!subject) {
      setEmailModalError("Saisissez un sujet avant d'envoyer le rapport.");
      return;
    }

    setIsSendingEmail(true);
    setEmailModalError("");
    setFeedback({ message: "", tone: "neutral" });

    try {
      const payload = await sendMeunerieReportEmail({
        dispatchDate,
        meunerieLookupKey: selectedMeunerieKey,
        recipientEmail,
        subject,
        timeMode: meunerieTimeMode,
      });

      setIsEmailModalOpen(false);
      setEmailForm({ recipientEmail: "", subject: "" });
      refreshReportHistory();
      setFeedback({
        message: typeof payload?.message === "string" && payload.message.trim()
          ? payload.message.trim()
          : `Le rapport Meunerie a été envoyé à ${recipientEmail}.`,
        tone: "success",
      });
    } catch (error) {
      const errorMessage = isExportApiError(error)
        ? error.message
        : error instanceof Error && error.message
          ? error.message
          : "Le rapport Meunerie n'a pas pu être envoyé par courriel.";

      setEmailModalError(errorMessage);
    } finally {
      setIsSendingEmail(false);
    }
  }, [dispatchDate, emailForm.recipientEmail, emailForm.subject, isSendingEmail, meunerieEmailDisabledReason, meunerieTimeMode, refreshReportHistory, selectedMeunerieKey]);

  const handleOpenTruckSheetEmailModal = React.useCallback(() => {
    if (!canOpenTruckSheetEmailModal) {
      return;
    }

    const driverName = normalizeInlineText(selectedTruck?.driverName);
    const subjectTemplate = truckSheetEmailTemplate.subjectTemplate || DEFAULT_TRUCK_SHEET_EMAIL_SUBJECT_TEMPLATE;
    const messageTemplate = truckSheetEmailTemplate.messageTemplate || DEFAULT_TRUCK_SHEET_EMAIL_MESSAGE_TEMPLATE;
    setTruckSheetEmailForm({
      recipientEmail: selectedTruckCourriel,
      subject: renderTruckSheetEmailTemplate(subjectTemplate, { dispatchDate, driverName })
        || buildDefaultTruckSheetEmailSubject({ dispatchDate, driverName }),
      message: renderTruckSheetEmailTemplate(messageTemplate, { dispatchDate, driverName })
        || buildDefaultTruckSheetEmailMessage({ dispatchDate, driverName }),
      onCallDispatcher: truckSheetOnCallDispatcher,
    });
    setTruckSheetEmailModalError("");
    setTruckSheetEmailFeedback({ message: "", tone: "neutral" });
    setIsTruckSheetEmailModalOpen(true);
  }, [
    canOpenTruckSheetEmailModal,
    dispatchDate,
    selectedTruck?.driverName,
    selectedTruck?.label,
    selectedTruckCourriel,
    truckSheetEmailTemplate.messageTemplate,
    truckSheetEmailTemplate.subjectTemplate,
    truckSheetOnCallDispatcher,
  ]);

  const handleOpenTruckSheetEmailTemplateModal = React.useCallback(() => {
    if (!canEditTruckSheetEmailTemplate || isLoadingTruckSheetEmailTemplate) {
      return;
    }

    setTruckSheetEmailTemplateForm({
      subjectTemplate: truckSheetEmailTemplate.subjectTemplate || DEFAULT_TRUCK_SHEET_EMAIL_SUBJECT_TEMPLATE,
      messageTemplate: truckSheetEmailTemplate.messageTemplate || DEFAULT_TRUCK_SHEET_EMAIL_MESSAGE_TEMPLATE,
    });
    setTruckSheetEmailTemplateError("");
    setIsTruckSheetEmailTemplateModalOpen(true);
  }, [canEditTruckSheetEmailTemplate, isLoadingTruckSheetEmailTemplate, truckSheetEmailTemplate.messageTemplate, truckSheetEmailTemplate.subjectTemplate]);

  const handleCloseTruckSheetEmailModal = React.useCallback(() => {
    if (isSendingTruckSheetEmail) {
      return;
    }

    setTruckSheetEmailModalError("");
    setIsTruckSheetEmailModalOpen(false);
  }, [isSendingTruckSheetEmail]);

  const handleCloseTruckSheetEmailTemplateModal = React.useCallback(() => {
    if (isSavingTruckSheetEmailTemplate) {
      return;
    }

    setTruckSheetEmailTemplateError("");
    setIsTruckSheetEmailTemplateModalOpen(false);
  }, [isSavingTruckSheetEmailTemplate]);

  const handleSaveTruckSheetEmailTemplate = React.useCallback(async () => {
    if (isSavingTruckSheetEmailTemplate) {
      return;
    }

    const subjectTemplate = truckSheetEmailTemplateForm.subjectTemplate.trim();
    const messageTemplate = truckSheetEmailTemplateForm.messageTemplate.trim();
    if (!subjectTemplate || !messageTemplate) {
      setTruckSheetEmailTemplateError("Le sujet et le message du modèle sont obligatoires.");
      return;
    }

    setIsSavingTruckSheetEmailTemplate(true);
    setTruckSheetEmailTemplateError("");
    try {
      const payload = await saveTruckSheetEmailTemplate({ subjectTemplate, messageTemplate });
      const savedTemplate = {
        subjectTemplate: typeof payload?.subject_template === "string" && payload.subject_template.trim()
          ? payload.subject_template
          : subjectTemplate,
        messageTemplate: typeof payload?.message_template === "string" && payload.message_template.trim()
          ? payload.message_template
          : messageTemplate,
      };
      setTruckSheetEmailTemplate(savedTemplate);
      setTruckSheetEmailTemplateForm(savedTemplate);
      setIsTruckSheetEmailTemplateModalOpen(false);
      setTruckSheetEmailFeedback({
        message: "Le modèle de courriel a été enregistré pour tous les répartiteurs.",
        tone: "success",
      });
    } catch (error) {
      setTruckSheetEmailTemplateError(
        error instanceof Error && error.message
          ? error.message
          : "Le modèle de courriel n'a pas pu être enregistré.",
      );
    } finally {
      setIsSavingTruckSheetEmailTemplate(false);
    }
  }, [isSavingTruckSheetEmailTemplate, truckSheetEmailTemplateForm.messageTemplate, truckSheetEmailTemplateForm.subjectTemplate]);

  const handleSendTruckSheetEmail = React.useCallback(async () => {
    if (isSendingTruckSheetEmail) {
      return;
    }

    const recipientEmail = normalizeInlineText(truckSheetEmailForm.recipientEmail);
    const subject = normalizeInlineText(truckSheetEmailForm.subject);
    const message = typeof truckSheetEmailForm.message === "string" ? truckSheetEmailForm.message.trim() : "";
    const onCallDispatcher = normalizeInlineText(truckSheetEmailForm.onCallDispatcher);

    if (truckSheetEmailDisabledReason) {
      setTruckSheetEmailModalError(truckSheetEmailDisabledReason);
      return;
    }

    if (!isValidEmailAddress(recipientEmail)) {
      setTruckSheetEmailModalError("Saisissez une adresse courriel destinataire valide.");
      return;
    }

    if (!subject) {
      setTruckSheetEmailModalError("Saisissez un sujet avant d'envoyer l'horaire chauffeur.");
      return;
    }

    if (!message) {
      setTruckSheetEmailModalError("Le message du courriel est vide.");
      return;
    }

    setIsSendingTruckSheetEmail(true);
    setTruckSheetEmailModalError("");
    setTruckSheetEmailFeedback({ message: "", tone: "neutral" });
    rememberTruckSheetOnCallDispatcher(onCallDispatcher);

    try {
      const payload = await sendTruckSheetReportEmail({
        dispatchDate,
        dispatchPeriod,
        fitOnePage: truckSheetPdfFitOnePage,
        message,
        notes: truckSheetPdfNote,
        onCallDispatcher,
        orientation: truckSheetPdfOrientation,
        recipientEmail,
        showCompartments: truckSheetPdfShowCompartments,
        showMap: truckSheetPdfShowMap,
        subject,
        truckId: effectiveSelectedTruckId,
      });

      setIsTruckSheetEmailModalOpen(false);
      setTruckSheetEmailForm({ recipientEmail: "", subject: "", message: "" });
      refreshReportHistory();
      setTruckSheetEmailFeedback({
        message: typeof payload?.message === "string" && payload.message.trim()
          ? payload.message.trim()
          : `L'horaire chauffeur a été envoyé à ${recipientEmail}.`,
        tone: "success",
      });
    } catch (error) {
      const errorMessage = isExportApiError(error)
        ? error.message
        : error instanceof Error && error.message
          ? error.message
          : "L'horaire chauffeur n'a pas pu être envoyé par courriel.";

      setTruckSheetEmailModalError(errorMessage);
    } finally {
      setIsSendingTruckSheetEmail(false);
    }
  }, [
    dispatchDate,
    dispatchPeriod,
    effectiveSelectedTruckId,
    isSendingTruckSheetEmail,
    refreshReportHistory,
    rememberTruckSheetOnCallDispatcher,
    truckSheetEmailDisabledReason,
    truckSheetEmailForm.onCallDispatcher,
    truckSheetEmailForm.recipientEmail,
    truckSheetEmailForm.message,
    truckSheetEmailForm.subject,
    truckSheetPdfFitOnePage,
    truckSheetPdfNote,
    truckSheetPdfOrientation,
    truckSheetPdfShowCompartments,
    truckSheetPdfShowMap,
  ]);

  const handleGenerateTruckPreloadReport = React.useCallback(async ({
    employeeId = "",
    notes = "",
    truckIds = [],
  } = {}) => {
    if (!dispatchDate) {
      setPreloadReportFeedback({
        message: "Sélectionnez une date de dispatch avant de générer le rapport.",
        tone: "danger",
      });
      return;
    }

    if (!hasSnapshot) {
      setPreloadReportFeedback({
        message: "Aucun dispatch calculé n'est disponible pour cette date.",
        tone: "danger",
      });
      return;
    }

    if (preloadTruckCount <= 0) {
      setPreloadReportFeedback({
        message: "Aucun camion avec commande préchargée la veille n'est disponible pour cette date.",
        tone: "danger",
      });
      return;
    }

    const normalizedEmployeeId = normalizeInlineText(employeeId);
    if (!normalizedEmployeeId) {
      setPreloadReportFeedback({
        message: "Sélectionnez un employé de soir avant de générer le rapport.",
        tone: "danger",
      });
      return;
    }

    const normalizedTruckIds = buildNormalizedIdList(truckIds);
    if (normalizedTruckIds.length <= 0) {
      setPreloadReportFeedback({
        message: "Cochez au moins un camion en préchargement avant de générer le rapport.",
        tone: "danger",
      });
      return;
    }

    let documentWindow = null;
    setIsGeneratingTruckPreloadReport(true);
    setPreloadReportFeedback({ message: "", tone: "neutral" });

    try {
      documentWindow = openExportDocumentWindow({
        detail: `${normalizedTruckIds.length} camion${normalizedTruckIds.length > 1 ? "s" : ""} • ${dispatchDate}`,
        eyebrow: "Préchargements camions",
        message: normalizedTruckIds.length > 1
          ? "Nous préparons un PDF groupé avec une page dédiée pour chaque camion en préchargement que vous avez coché."
          : "Nous préparons le PDF du camion en préchargement sélectionné.",
        preferredWidth: 1180,
        title: "Préparation du rapport Préchargements camions",
        windowName: "grain-truck-preload-report",
      });

      await downloadTruckPreloadReport({
        dispatchDate,
        documentWindow,
        employeeId: normalizedEmployeeId,
        notes,
        showCompartments: truckPreloadShowCompartments,
        truckIds: normalizedTruckIds,
      });

      refreshReportHistory();
      setPreloadReportFeedback({
        message: "Le rapport Préchargements camions est prêt et a été envoyé vers le navigateur.",
        tone: "success",
      });
    } catch (error) {
      const errorMessage = isExportApiError(error)
        ? error.message
        : error instanceof Error && error.message
          ? error.message
          : "Le rapport Préchargements camions n'a pas pu être généré.";

      if (documentWindow) {
        markExportDocumentWindowFailed(documentWindow, {
          detail: errorMessage,
          eyebrow: "Préchargements camions",
          message: "Le rapport Préchargements camions n'a pas pu être généré.",
          title: "Export interrompu",
        });
      }

      setPreloadReportFeedback({
        message: errorMessage,
        tone: "danger",
      });
    } finally {
      setIsGeneratingTruckPreloadReport(false);
    }
  }, [dispatchDate, hasSnapshot, preloadTruckCount, refreshReportHistory, truckPreloadShowCompartments]);

  const handleConfirmTruckPreloadReportModal = React.useCallback(() => {
    const normalizedEmployeeId = normalizeInlineText(truckPreloadReportEmployeeId);
    if (!normalizedEmployeeId) {
      setPreloadReportFeedback({
        message: "Sélectionnez un employé de soir avant de générer le rapport.",
        tone: "danger",
      });
      return;
    }

    const normalizedSelectedTruckIds = buildNormalizedIdList(truckPreloadReportSelectedTruckIds);
    if (normalizedSelectedTruckIds.length <= 0) {
      setPreloadReportFeedback({
        message: "Cochez au moins un camion en préchargement avant de générer le rapport.",
        tone: "danger",
      });
      return;
    }

    setIsTruckPreloadReportModalOpen(false);
    void handleGenerateTruckPreloadReport({
      employeeId: normalizedEmployeeId,
      notes: truckPreloadReportNote,
      truckIds: normalizedSelectedTruckIds,
    });
  }, [handleGenerateTruckPreloadReport, truckPreloadReportEmployeeId, truckPreloadReportNote, truckPreloadReportSelectedTruckIds]);

  return (
    <div className={joinClasses([
      "reports-page mx-auto w-full px-4 pb-4 pt-0",
      REPORTS_SURFACE_WIDTH_CLASS_NAME,
    ])}>
      <div className="reports-content">
        <WorkspacePageHeader
          className="reports-hero"
          titleAs="h1"
          icon={FileText}
          actions={(
            <label className="reports-dispatch-date reports-field">
              <span className="reports-label inline-flex items-center gap-2">
                <CalendarDays size={14} aria-hidden="true" />
                Date de dispatch
              </span>
              <input
                type="date"
                max="9999-12-31"
                value={dispatchDateInput}
                onBlur={() => setDispatchDateInput(dispatchDate)}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setDispatchDateInput(nextValue);
                  // Pendant la frappe de l'année, le navigateur passe par vide, 0002, 0020, 0202 : on attend l'année complète.
                  if (!nextValue || Number(nextValue.slice(0, 4)) < 2000) {
                    return;
                  }
                  setDispatchDate(nextValue);
                  setFeedback({ message: "", tone: "neutral" });
                  setPreloadReportFeedback({ message: "", tone: "neutral" });
                }}
                className="h-8 rounded-[4px] border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-border-accent-muted focus:ring-2 focus:ring-border-accent-subtle"
              />
            </label>
          )}
          title="Centre de rapports"
          description={<span className="reports-hero-subtitle">Préparez, exportez et partagez vos rapports.</span>}
        />

        {isHistoryFullPage ? (
          <ReportsHistorySection
            errorMessage={historyErrorMessage}
            historyItems={historyItems}
            isCollapsed={false}
            isFullPage
            isLoading={isHistoryLoading}
            isRefreshing={isHistoryRefreshing}
            onCloseFullPage={handleCloseFullHistory}
          />
        ) : (
        <section className="reports-workspace" aria-label="Rapports disponibles">
          <div className="reports-grid">
            <article className="reports-card">
              <div className="reports-card-header">
                <div className={REPORTS_TITLE_ICON_CLASS_NAME}>
                  <Wheat size={25} aria-hidden="true" />
                </div>
                <div className="grid gap-0.5">
                  <h2 className="reports-card-title text-slate-950">Rapport Meunerie</h2>
                  <p className="text-[0.82rem] leading-[1.35] text-slate-600">
                    Les commandes regroupées par meunerie.
                  </p>
                </div>
              </div>

              <div className={REPORTS_ACTION_ROW_CLASS_NAME}>
                <label className={REPORTS_FILTER_FIELD_CLASS_NAME}>
                  <span className={REPORTS_FILTER_LABEL_CLASS_NAME}>Meunerie</span>
                  <select
                    value={selectedMeunerieKey}
                    onChange={(event) => {
                      setSelectedMeunerieKey(event.target.value);
                      setFeedback({ message: "", tone: "neutral" });
                    }}
                    disabled={isOptionsLoading || meunerieOptions.length === 0}
                    className="min-h-10 min-w-0 w-full rounded-[4px] border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-border-accent-muted focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                  >
                    <option value="">{isOptionsLoading ? "Chargement des meuneries..." : "Sélectionner une meunerie"}</option>
                    {meunerieSelectOptions.map((item) => (
                      <option key={item.lookup_key} value={item.lookup_key}>{item.label}</option>
                    ))}
                  </select>
                </label>

                <div className="reports-options">
                  <div className={REPORTS_COMPACT_CONTROL_CLASS_NAME}>
                    <span className={REPORTS_FILTER_LABEL_CLASS_NAME}>Heures</span>
                    <div className="reports-option-group">
                      {MEUNERIE_TIME_MODE_OPTIONS.map((option) => {
                        const isSelected = meunerieTimeMode === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            aria-pressed={isSelected}
                            title={option.title}
                            className={joinClasses([
                              "reports-option-button",
                              isSelected
                                ? "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong"
                                : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900",
                            ])}
                            onClick={() => {
                              setMeunerieTimeMode(option.value);
                            }}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        title={meunerieManualTimesDisabledReason || "Saisir les heures de chargement du rapport"}
                        disabled={Boolean(meunerieManualTimesDisabledReason)}
                        className="reports-option-button gap-1.5 border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
                        onClick={() => {
                          setIsMeunerieManualTimesOpen(true);
                        }}
                      >
                        <Pencil aria-hidden="true" size={14} strokeWidth={2.25} />
                        Saisir les heures
                      </button>
                    </div>
                  </div>
                </div>

                <div className={REPORTS_ACTION_SLOT_CLASS_NAME}>
                  <ReportsDisabledActionHoverCard
                    actionLabel="Générer le PDF"
                    disabledReason={meunerieGenerateDisabledReason}
                    fitContent
                  >
                    <ActionButton
                      tone="optimizer"
                      size="sm"
                      onClick={() => {
                        void handleGenerateReport();
                      }}
                      disabled={!canGenerateReport}
                      isLoading={isGenerating}
                      loadingLabel="Génération..."
                      className={REPORTS_PRIMARY_ACTION_CLASS_NAME}
                    >
                      Générer le PDF
                    </ActionButton>
                  </ReportsDisabledActionHoverCard>

                  {canEmailReports ? (
                    <ReportsDisabledActionHoverCard
                      actionLabel="Envoyer par courriel"
                      disabledReason={meunerieEmailActionDisabledReason}
                      fitContent
                    >
                      <ReportsEmailTriggerButton
                        onClick={handleOpenMeunerieEmailModal}
                        disabled={!canOpenMeunerieEmailModal || isEmailModalOpen}
                      />
                    </ReportsDisabledActionHoverCard>
                  ) : null}
                  <ReportEmailActions
                    className={REPORTS_EMAIL_ACTIONS_CLASS_NAME}
                    immediate={false}
                    items={[defaultReportEmailItem("report", "meunerie", {
                      date_mode: reportEmailDate ? "fixed" : "today",
                      // The Jour report already carries the evening dispatch in its « Soir » section.
                      dispatch_period: "day",
                      fixed_date: reportEmailDate,
                      meunerie_lookup_key: selectedMeunerieKey || ALL_MEUNERIES_LOOKUP_KEY,
                      meunerie_time_mode: meunerieTimeMode,
                    })]}
                  />
                </div>
              </div>

              {optionsError ? (
                <div className="rounded-[4px] border border-border-danger-subtle bg-danger-soft px-3 py-2 text-[0.82rem] leading-5 text-danger">
                  {optionsError}
                </div>
              ) : null}

              {!optionsError && !isOptionsLoading && meunerieOptions.length === 0 ? (
                <div className="rounded-[4px] border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-[0.82rem] leading-5 text-slate-600">
                  Aucune meunerie dans l'onglet « Clients » (« Meunerie » à Oui).
                </div>
              ) : null}

              {feedback.message ? (
                <div className={joinClasses([
                  "rounded-[4px] px-3 py-2 text-[0.82rem] leading-5",
                  resolveFeedbackToneClassName(feedback.tone),
                ])}>
                  {feedback.message}
                </div>
              ) : null}
            </article>

            <article className="reports-card">
              <div className="reports-card-header">
                <div className={REPORTS_TITLE_ICON_CLASS_NAME}>
                  <UserRound size={25} aria-hidden="true" />
                </div>
                <div className="grid gap-0.5">
                  <h2 className="reports-card-title text-slate-950">Horaire chauffeur</h2>
                  <p className="text-[0.82rem] leading-[1.35] text-slate-600">Une fiche par chauffeur, seule ou en PDF groupé.</p>
                </div>
              </div>

              <div className={REPORTS_ACTION_ROW_CLASS_NAME}>
                <div className="reports-settings">
                  <div className={REPORTS_FILTER_FIELD_CLASS_NAME}>
                    <ReportsDriverSelect
                      value={selectedTruckId}
                      onChange={setSelectedTruckId}
                      disabled={!dispatchDate || !hasSnapshot || isSnapshotLoading || driverOptions.length === 0}
                      options={truckSelectOptions}
                      placeholder={isSnapshotLoading
                        ? "Chargement des chauffeurs..."
                        : driverOptions.length > 0 ? "Sélectionner un chauffeur" : "Aucun chauffeur avec dispatch"}
                    />
                  </div>

                  <div className="reports-options">
                    <div className={REPORTS_COMPACT_CONTROL_CLASS_NAME}>
                      <span className={REPORTS_FILTER_LABEL_CLASS_NAME}>Période</span>
                      <div className="reports-option-group">
                        {DISPATCH_PERIOD_OPTIONS.map((option) => {
                          const isSelected = normalizeDispatchPeriod(dispatchPeriod) === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              aria-pressed={isSelected}
                              className={joinClasses([
                                "reports-option-button",
                                isSelected
                                  ? "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong"
                                  : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900",
                              ])}
                              onClick={() => {
                                setDispatchPeriod(option.value);
                              }}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className={REPORTS_COMPACT_CONTROL_CLASS_NAME}>
                      <span className={REPORTS_FILTER_LABEL_CLASS_NAME}>Format</span>
                      <div className="reports-option-group">
                        {TRUCK_SHEET_ORIENTATION_OPTIONS.map((option) => {
                          const isSelected = truckSheetPdfOrientation === option.value;
                          const Icon = option.icon;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              aria-label={option.ariaLabel}
                              aria-pressed={isSelected}
                              title={option.ariaLabel}
                              className={joinClasses([
                                "reports-option-button",
                                isSelected
                                  ? "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong"
                                  : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900",
                              ])}
                              onClick={() => {
                                setTruckSheetPdfOrientation(option.value);
                              }}
                            >
                              <Icon aria-hidden="true" size={16} strokeWidth={2.25} />
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className={REPORTS_COMPACT_CONTROL_CLASS_NAME}>
                      <span className={REPORTS_FILTER_LABEL_CLASS_NAME}>Hauteur</span>
                      <div className="reports-option-group">
                        <button
                          type="button"
                          aria-pressed={truckSheetPdfFitOnePage}
                          title="Compresse la hauteur pour tenter de tout faire tenir sur une page"
                          className={joinClasses([
                            "reports-option-button",
                            truckSheetPdfFitOnePage
                              ? "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong"
                              : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900",
                          ])}
                          onClick={() => {
                            setTruckSheetPdfFitOnePage((current) => !current);
                          }}
                        >
                          Une page
                        </button>
                      </div>
                    </div>

                    {canChooseTruckSheetCompartments ? (
                      <div className={REPORTS_COMPACT_CONTROL_CLASS_NAME}>
                        <span className={REPORTS_FILTER_LABEL_CLASS_NAME}>Compartiments</span>
                        <div className="reports-option-group">
                          {TRUCK_SHEET_COMPARTMENT_OPTIONS.map((option) => {
                            const isSelected = truckSheetPdfShowCompartments === option.value;
                            return (
                              <button
                                key={option.label}
                                type="button"
                                aria-pressed={isSelected}
                                title={option.title}
                                className={joinClasses([
                                  "reports-option-button",
                                  isSelected
                                    ? "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong"
                                    : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900",
                                ])}
                                onClick={() => {
                                  setTruckSheetPdfShowCompartments(option.value);
                                }}
                              >
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}

                    {truckSheetPdfOrientation === "portrait" ? (
                      <div className={REPORTS_COMPACT_CONTROL_CLASS_NAME}>
                        <span className={REPORTS_FILTER_LABEL_CLASS_NAME}>Carte</span>
                        <div className="reports-option-group">
                          {TRUCK_SHEET_MAP_OPTIONS.map((option) => {
                            const isSelected = truckSheetPdfShowMap === option.value;
                            return (
                              <button
                                key={option.label}
                                type="button"
                                aria-pressed={isSelected}
                                title={option.title}
                                className={joinClasses([
                                  "reports-option-button",
                                  isSelected
                                    ? "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong"
                                    : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900",
                                ])}
                                onClick={() => {
                                  setTruckSheetPdfShowMap(option.value);
                                }}
                              >
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className={REPORTS_ACTION_SLOT_CLASS_NAME}>
                  <ReportsDisabledActionHoverCard
                    actionLabel={truckSheetPdfActionLabel}
                    disabledReason={truckSheetPrepareDisabledReason}
                    fitContent
                  >
                    <TruckSheetPdfActionButton
                      disabled={!dispatchDate || !hasSnapshot || !selectedTruckId || isTruckSheetPdfBusy || isTruckSheetPdfBlocked}
                      isLoading={isTruckSheetPdfBusy}
                      label={truckSheetPdfActionLabel}
                      onClick={() => {
                        handleTruckSheetPdfAction();
                      }}
                    />
                  </ReportsDisabledActionHoverCard>
                  {canEmailReports ? (
                    <ReportsDisabledActionHoverCard
                      actionLabel="Envoyer par courriel"
                      disabledReason={truckSheetEmailActionDisabledReason}
                      fitContent
                    >
                      <ReportsEmailTriggerButton
                        onClick={handleOpenTruckSheetEmailModal}
                        disabled={!canOpenTruckSheetEmailModal || isTruckSheetEmailModalOpen || isTruckSheetPdfBusy}
                      />
                    </ReportsDisabledActionHoverCard>
                  ) : null}
                  <div className="reports-planning-actions">
                    <ReportEmailActions
                      className={REPORTS_EMAIL_ACTIONS_CLASS_NAME}
                      immediate={false}
                      items={[defaultReportEmailItem("report", "truck_sheets", {
                        date_mode: reportEmailDate ? "fixed" : "today",
                        dispatch_period: normalizeDispatchPeriod(dispatchPeriod),
                        fixed_date: reportEmailDate,
                        fit_one_page: truckSheetPdfFitOnePage,
                        notes: truckSheetPdfNote,
                        on_call_dispatcher: truckSheetOnCallDispatcher,
                        orientation: truckSheetPdfOrientation,
                        show_compartments: truckSheetPdfShowCompartments,
                        show_map: truckSheetPdfShowMap,
                        truck_ids: effectiveSelectedTruckId ? [effectiveSelectedTruckId] : [],
                      })]}
                    />
                    {canEditTruckSheetEmailTemplate ? (
                      <div className="reports-customize-action">
                        <ReportsDisabledActionHoverCard
                          actionLabel="Personnaliser"
                          disabledReason={truckSheetTemplateDisabledReason}
                          fitContent
                        >
                          <button
                            type="button"
                            title="Personnaliser le modèle de courriel"
                            onClick={handleOpenTruckSheetEmailTemplateModal}
                            disabled={isLoadingTruckSheetEmailTemplate || isTruckSheetEmailTemplateModalOpen || isTruckSheetPdfBusy}
                            className="inline-flex min-h-8 items-center justify-center gap-1.5 rounded-[4px] border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <Pencil aria-hidden="true" size={14} />
                            <span className={truckSheetActionCount > 3 ? "sr-only" : undefined}>Personnaliser</span>
                          </button>
                        </ReportsDisabledActionHoverCard>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              {truckEmailContactsError ? (
                <div className="rounded-[4px] border border-border-danger-subtle bg-danger-soft px-3 py-2 text-[0.82rem] leading-5 text-danger">
                  {truckEmailContactsError}
                </div>
              ) : null}

              {truckSheetEmailFeedback.message ? (
                <div className={joinClasses([
                  "rounded-[4px] px-3 py-2 text-[0.82rem] leading-5",
                  resolveFeedbackToneClassName(truckSheetEmailFeedback.tone),
                ])}>
                  {truckSheetEmailFeedback.message}
                </div>
              ) : null}
            </article>

            <article className="reports-card">
              <div className="reports-card-header">
                <div className={REPORTS_TITLE_ICON_CLASS_NAME}>
                  <Truck size={25} aria-hidden="true" />
                </div>
                <div className="grid gap-0.5">
                  <h2 className="reports-card-title text-slate-950">Préchargements camions</h2>
                  <p className="text-[0.82rem] leading-[1.35] text-slate-600">Les chargements à préparer avant le départ.</p>
                </div>
              </div>

              <div className={REPORTS_ACTION_ROW_CLASS_NAME}>
                <label className={REPORTS_FILTER_FIELD_CLASS_NAME}>
                  <span className={REPORTS_FILTER_LABEL_CLASS_NAME}>Camions en préchargement</span>
                  <select
                    aria-label="Camions en préchargement"
                    disabled={!dispatchDate || !hasSnapshot || isSnapshotLoading || preloadTruckCount === 0}
                    className="min-h-10 min-w-0 w-full rounded-[4px] border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-border-accent-muted focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                  >
                    <option value="">
                      {isSnapshotLoading
                        ? "Chargement des camions..."
                        : preloadTruckCount > 0
                          ? `${preloadTruckCount} camion${preloadTruckCount > 1 ? "s" : ""} détecté${preloadTruckCount > 1 ? "s" : ""}`
                          : "Aucun camion en préchargement"}
                    </option>
                  </select>
                </label>

                <div className="reports-options">
                  {canChooseTruckPreloadCompartments ? (
                    <div className={REPORTS_COMPACT_CONTROL_CLASS_NAME}>
                      <span className={REPORTS_FILTER_LABEL_CLASS_NAME}>Compartiments</span>
                      <div className="reports-option-group">
                        {TRUCK_PRELOAD_COMPARTMENT_OPTIONS.map((option) => {
                          const isSelected = truckPreloadShowCompartments === option.value;
                          return (
                            <button
                              key={option.label}
                              type="button"
                              aria-pressed={isSelected}
                              title={option.title}
                              className={joinClasses([
                                "reports-option-button",
                                isSelected
                                  ? "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong"
                                  : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900",
                              ])}
                              onClick={() => {
                                setTruckPreloadShowCompartments(option.value);
                              }}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className={REPORTS_ACTION_SLOT_CLASS_NAME}>
                  <ReportsDisabledActionHoverCard
                    actionLabel="Générer le PDF"
                    disabledReason={truckPreloadGenerateDisabledReason}
                    fitContent
                  >
                    <ActionButton
                      tone="optimizer"
                      size="sm"
                      onClick={() => {
                        handleOpenTruckPreloadReportModal();
                      }}
                      disabled={!canGenerateTruckPreloadReport}
                      isLoading={isGeneratingTruckPreloadReport}
                      loadingLabel="Génération..."
                      className={REPORTS_PRIMARY_ACTION_CLASS_NAME}
                    >
                      Générer le PDF
                    </ActionButton>
                  </ReportsDisabledActionHoverCard>
                  <ReportEmailActions
                    className={REPORTS_EMAIL_ACTIONS_CLASS_NAME}
                    items={[defaultReportEmailItem("report", "truck_preloads", {
                      date_mode: reportEmailDate ? "fixed" : "today",
                      dispatch_period: normalizeDispatchPeriod(dispatchPeriod),
                      employee_id: truckPreloadReportEmployeeId || null,
                      fixed_date: reportEmailDate,
                      notes: truckPreloadReportNote,
                      show_compartments: truckPreloadShowCompartments,
                      truck_ids: truckPreloadReportSelectedTruckIds.length > 0
                        ? truckPreloadReportSelectedTruckIds
                        : [],
                    })]}
                  />
                </div>
              </div>

              {preloadReportFeedback.message ? (
                <div className={joinClasses([
                  "rounded-[4px] px-3 py-2 text-[0.82rem] leading-5",
                  resolveFeedbackToneClassName(preloadReportFeedback.tone),
                ])}>
                  {preloadReportFeedback.message}
                </div>
              ) : null}
            </article>

            {isAccessReady && canExportDataManagerTable(access, "orders") ? (
              <ReportsOrdersPdfCard
                dispatchDate={dispatchDate}
                onExported={refreshReportHistory}
              />
            ) : null}

            <ReportsDispatchGanttCard dispatchDate={dispatchDate} dispatchPeriod={dispatchPeriod}
              hasSnapshot={hasSnapshot} isLoading={snapshotState.status === "loading"}
              onDispatchPeriodChange={setDispatchPeriod} onExported={refreshReportHistory} />

            <article className="reports-card">
              <div className="reports-card-header">
                <div className={REPORTS_TITLE_ICON_CLASS_NAME}>
                  <Sheet size={25} aria-hidden="true" />
                </div>
                <div className="grid gap-0.5">
                  <h2 className="reports-card-title text-slate-950">Export détaillé</h2>
                  <p className="text-[0.82rem] leading-[1.35] text-slate-600">Les résultats détaillés du dispatch en XLSX.</p>
                </div>
              </div>

              <div className={REPORTS_ACTION_ROW_CLASS_NAME}>
                <div className="reports-export-status" aria-live="polite">
                  <span className="text-[0.68rem] font-black uppercase tracking-[0.16em] text-slate-500">Résultats exportés</span>
                  <p className="text-[0.92rem] font-semibold tracking-[-0.02em] text-slate-900">
                    {dispatchDate ? `Dispatch ${dispatchDate}` : "Date de dispatch requise"}
                  </p>
                  <p className="text-[0.8rem] leading-5 text-slate-600">{resultsExportNote}</p>
                </div>

                <div className={REPORTS_ACTION_SLOT_CLASS_NAME}>
                  <ReportsDisabledActionHoverCard
                    actionLabel={isResultsExportReady ? "Télécharger le XLSX" : "Préparer le XLSX"}
                    disabledReason={resultsExportDisabledReason}
                    fitContent
                  >
                    <ActionButton
                      tone="optimizer"
                      size="sm"
                      onClick={() => {
                        handleResultsExportAction();
                      }}
                      disabled={!dispatchDate || !hasSnapshot || isResultsExportBusy}
                      isLoading={isResultsExportBusy}
                      loadingLabel="Préparation..."
                      className={REPORTS_PRIMARY_ACTION_CLASS_NAME}
                    >
                      {isResultsExportReady ? "Télécharger le XLSX" : "Préparer le XLSX"}
                    </ActionButton>
                  </ReportsDisabledActionHoverCard>
                  <ReportEmailActions
                    className={REPORTS_EMAIL_ACTIONS_CLASS_NAME}
                    items={[defaultReportEmailItem("report", "results", {
                      date_mode: reportEmailDate ? "fixed" : "today",
                      dispatch_period: normalizeDispatchPeriod(dispatchPeriod),
                      fixed_date: reportEmailDate,
                    })]}
                  />
                </div>
              </div>
            </article>

            {/* En dernier : ouverte, la carte prend toute une rangée sans laisser de trou dans la grille. */}
            {isAccessReady && (hasPermission(access, ACCESS_PERMISSION_KEYS.dispatchGeotabRealRead)
              || hasPermission(access, ACCESS_PERMISSION_KEYS.geotabRead)) ? <ReportsGeotabOrdersCard dispatchDate={dispatchDate} /> : null}
          </div>
          <ReportsHistorySection
            errorMessage={historyErrorMessage}
            historyItems={historyItems}
            isCollapsed={isHistoryCollapsed}
            isLoading={isHistoryLoading}
            isRefreshing={isHistoryRefreshing}
            onOpenFullPage={handleOpenFullHistory}
            onToggleCollapsed={() => {
              setIsHistoryCollapsed((currentValue) => !currentValue);
            }}
          />
        </section>
        )}
      </div>

      <MeunerieManualTimesModal
        dispatchDate={dispatchDate}
        isOpen={isMeunerieManualTimesOpen}
        meunerieLabel={selectedMeunerieLabel}
        meunerieLookupKey={selectedMeunerieKey}
        onClose={() => {
          setIsMeunerieManualTimesOpen(false);
        }}
        onSaved={(message) => {
          setMeunerieTimeMode(MEUNERIE_TIME_MODE_MANUAL);
          setFeedback({ message: `${message} Le mode « Heures : Manuelles » est activé.`, tone: "success" });
        }}
      />
      <MeunerieEmailModal
        dispatchDate={dispatchDate}
        errorMessage={emailModalError}
        isOpen={isEmailModalOpen}
        isSending={isSendingEmail}
        meunerieLabel={selectedMeunerieLabel}
        onClose={handleCloseMeunerieEmailModal}
        onRecipientEmailChange={(value) => {
          setEmailForm((currentValue) => ({
            ...currentValue,
            recipientEmail: value,
          }));
          setEmailModalError("");
        }}
        onSend={() => {
          void handleSendMeunerieEmail();
        }}
        onSubjectChange={(value) => {
          setEmailForm((currentValue) => ({
            ...currentValue,
            subject: value,
          }));
          setEmailModalError("");
        }}
        recipientEmail={emailForm.recipientEmail}
        subject={emailForm.subject}
      />
      <TruckSheetEmailModal
        dispatchDate={dispatchDate}
        dispatchPeriodLabel={dispatchPeriodLabel}
        driverName={selectedTruck?.driverName || ""}
        errorMessage={truckSheetEmailModalError}
        isOpen={isTruckSheetEmailModalOpen}
        isSending={isSendingTruckSheetEmail}
        onClose={handleCloseTruckSheetEmailModal}
        onRecipientEmailChange={(value) => {
          setTruckSheetEmailForm((currentValue) => ({
            ...currentValue,
            recipientEmail: value,
          }));
          setTruckSheetEmailModalError("");
        }}
        onSend={() => {
          void handleSendTruckSheetEmail();
        }}
        message={truckSheetEmailForm.message}
        onCallDispatcher={truckSheetEmailForm.onCallDispatcher}
        onOnCallDispatcherChange={(value) => {
          setTruckSheetEmailForm((currentValue) => ({ ...currentValue, onCallDispatcher: value }));
        }}
        recipientEmail={truckSheetEmailForm.recipientEmail}
        scopeLabel={selectedTruck?.label || "Chauffeur sélectionné"}
        subject={truckSheetEmailForm.subject}
      />
      <TruckSheetEmailTemplateModal
        dispatchDate={dispatchDate}
        driverName={selectedTruck?.driverName || ""}
        errorMessage={truckSheetEmailTemplateError}
        isOpen={isTruckSheetEmailTemplateModalOpen}
        isSaving={isSavingTruckSheetEmailTemplate}
        messageTemplate={truckSheetEmailTemplateForm.messageTemplate}
        onClose={handleCloseTruckSheetEmailTemplateModal}
        onMessageTemplateChange={(value) => {
          setTruckSheetEmailTemplateForm((currentValue) => ({
            ...currentValue,
            messageTemplate: value,
          }));
          setTruckSheetEmailTemplateError("");
        }}
        onSave={() => {
          void handleSaveTruckSheetEmailTemplate();
        }}
        onSubjectTemplateChange={(value) => {
          setTruckSheetEmailTemplateForm((currentValue) => ({
            ...currentValue,
            subjectTemplate: value,
          }));
          setTruckSheetEmailTemplateError("");
        }}
        subjectTemplate={truckSheetEmailTemplateForm.subjectTemplate}
      />
      <TruckSheetNoteModal
        dispatchDate={dispatchDate}
        dispatchPeriodLabel={dispatchPeriodLabel}
        generateLabel="Générer le PDF"
        isOpen={isTruckSheetNoteModalOpen}
        note={truckSheetPdfNote}
        onCallDispatcher={truckSheetPdfOnCallDispatcher}
        onClose={handleCloseTruckSheetNoteModal}
        onGenerate={handleConfirmTruckSheetNoteModal}
        onNoteChange={setTruckSheetPdfNote}
        onOnCallDispatcherChange={setTruckSheetPdfOnCallDispatcher}
        scopeLabel={isAllTrucksSelected ? "Tous les chauffeurs" : (selectedTruck?.label || "Chauffeur sélectionné")}
      />
      <TruckPreloadReportModal
        dispatchDate={dispatchDate}
        employeeErrorMessage={truckPreloadNightEmployeeState.errorMessage}
        employees={truckPreloadNightEmployeeState.employees}
        generateLabel="Préparer le PDF"
        isLoadingEmployees={isTruckPreloadEmployeesLoading}
        isOpen={isTruckPreloadReportModalOpen}
        note={truckPreloadReportNote}
        onClose={handleCloseTruckPreloadReportModal}
        onEmployeeChange={setTruckPreloadReportEmployeeId}
        onGenerate={handleConfirmTruckPreloadReportModal}
        onNoteChange={setTruckPreloadReportNote}
        onSelectedTruckIdsChange={setTruckPreloadReportSelectedTruckIds}
        selectedEmployeeId={truckPreloadReportEmployeeId}
        selectedTruckIds={truckPreloadReportSelectedTruckIds}
        truckOptions={preloadTruckOptions}
      />
    </div>
  );
}
